import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { getSalesInvoices, saveSalesInvoice, getSalesInvoiceForReturn, getSalesReturnById, getNextSalesInvoiceNumber, getInvoiceForPrint, getCustomerAdvanceBalance, getCustomerOutstandingCredit } from "../../components/Api/GmsBilling";
import { checkDayClosing } from "../../components/Api/GmsDayClosing";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getProducts } from "../../components/Api/GmsProduct";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaPackages } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getTaxes } from "../../components/Api/GmsTax";
import { getCustomerMemberships } from "../../components/Api/GmsCustomerMembership";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { Eye, SquarePen, Receipt, Package, RotateCcw, Printer, User } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";
import QuickAddCustomer from "../../components/QuickAddCustomer";
import CustomerProfileModal from "../../components/CustomerProfileModal";
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';
import StockSelectionModal from "../../components/StockSelectionModal";
import StockDetailsModal from "../../components/StockDetailsModal";
import { checkStockSelectionLocks, deleteStockSelectionLock } from "../../components/Api/GmsStockSelectionLock";
import html2pdf from "html2pdf.js";
import BASE_URL from "../../config";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  Invoice_Id: 0,
  Invoice_No: "",
  Invoice_Date: new Date(),
  Invoice_CustomerId: null,
  Invoice_BusinessId: null,
  Invoice_BranchId: null,
  selectedProducts: [],
  selectedServices: [],
  selectedPackages: [],
  selectedPackageSeries: [],
  productQuantities: {},
  serviceQuantities: {},
  packageQuantities: {},
  packageSeriesQuantities: {},
  productPrices: {},
  servicePrices: {},
  packagePrices: {},
  packageSeriesPrices: {},
  productDiscounts: {},
  serviceDiscounts: {},
  packageDiscounts: {},
  packageSeriesDiscounts: {},
  productTaxes: {},
  serviceTaxes: {},
  packageTaxes: {},
  packageSeriesTaxes: {},
  Invoice_TotalQty: 0,
  Invoice_TotalAmount: 0,
  Invoice_DisAmount: 0,
  Invoice_TaxAmount: 0,
  Invoice_NetAmount: 0,
  Invoice_Type: "ca", // Cash by default
  PaidAmount: 0,
  CashAmount: null,
  CashlessAmount: null,
  PaymentMethod: "ca", // Cash by default
  AuthCode: "",
  CostAmount: 0,
  TotalCost: 0,
  discountPercentage: 0,
  overallDiscountPercentage: null,
  overallDiscountAmount: 0,
  // Fields for entity selection
  entityItems: [], // Array to store sequential entity items
  // Modal fields
  showEntityModal: false,
  selectedModalItems: {}, // {entityId: quantity}
  modalEntityType: '',
  modalSearchTerm: '', // Search within modal
  dropdownOpen: {},
  // Stock selection modal
  showStockModal: false,
  currentStockProduct: null,
  selectedStockData: {}, // {productId: stockData} // Track dropdown states for each item
  addButtonDropdownOpen: false, // Track add button dropdown state
  // Stock details modal fields
  showStockDetailsModal: false,
  currentStockDetailsProduct: null,
  currentStockDetails: [],
  // Customer membership modal fields
  showMembershipModal: false,
  selectedMembershipInstallments: {}, // {installmentId: {amount: value, checked: boolean}}
  customerMemberships: [], // Store customer memberships
  installmentNames: {}, // Store installment names
  entityNames: {}, // Store entity names {entityId: entityName}
  // Return mode fields
  preloadedStockItems: null, // Pre-loaded stock items for return mode
};

const emptyFilter = {
  Invoice_Id: null,
  Customer_Id: null,
  FromDate: null,
  ToDate: null,
  Invoice_Type: null,
  PaymentMethod: null,
};

export default function BillingPage() {
  // Helper function to format numeric values - returns empty string if 0, otherwise returns the value
  const formatNumericValue = (value) => {
    const numValue = parseFloat(value) || 0;
    return numValue === 0 ? '' : numValue;
  };

  // Add CSS for stock details modal z-index
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Force stock details modal to appear above everything */
      .stock-details-modal__wrapper {
        z-index: 2001 !important;
        position: fixed !important;
      }
      .stock-details-modal__dialog {
        z-index: 2001 !important;
      }
      .stock-details-modal__backdrop {
        z-index: 2000 !important;
      }
      /* Ensure SweetAlert stays below stock modal */
      .swal2-container {
        z-index: 1060 !important;
      }
      .swal2-backdrop-show {
        z-index: 1059 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [selectedCustomerFilterOption, setSelectedCustomerFilterOption] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageSeries, setPackageSeries] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  // Return mode states
  const [isReturnMode, setIsReturnMode] = useState(false);
  const [returnSalesInvoiceId, setReturnSalesInvoiceId] = useState(null);
  const [originalInvoiceNo, setOriginalInvoiceNo] = useState(null);
  const [originalInvoicePaymentMethod, setOriginalInvoicePaymentMethod] = useState(null);
  const [originalInvoiceType, setOriginalInvoiceType] = useState(null);
  // Invoice list type: 'normal' or 'return'
  const [invoiceListType, setInvoiceListType] = useState('normal');
  // Stock selection lock - unique form ID
  const [temporaryFormUniqueId, setTemporaryFormUniqueId] = useState(null);
  // Invoice print modal state
  const [showInvoicePrintModal, setShowInvoicePrintModal] = useState(false);
  const [invoicePrintData, setInvoicePrintData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  // Customer advance and outstanding credit state
  const [customerAdvanceBalance, setCustomerAdvanceBalance] = useState(null);
  const [customerOutstandingCredit, setCustomerOutstandingCredit] = useState(null);
  const [loadingCustomerInfo, setLoadingCustomerInfo] = useState(false);

  // Filter options
  const invoiceTypeOptions = [
    { value: "ca", label: "Cash" },
    { value: "cr", label: "Credit" }
  ];

  const paymentMethodOptions = [
    { value: "ca", label: "Cash" },
    { value: "cc", label: "Credit Card" },
    { value: "on", label: "Online" }
  ];

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsBilling');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsBilling');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsBilling');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsBilling');
  const isAdmin = isUserAdmin();

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  // Generate unique form ID for stock selection lock
  const generateTemporaryFormUniqueId = () => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, ''); // HHMMSS
    const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const prefix = isReturnMode ? 'SR' : 'SI'; // SR for Sales Return, SI for Sales Invoice
    const formId = `${prefix}-${dateStr}-${timeStr}-${sequence}`;
    
    // Store form ID in sessionStorage so we can clean it up on refresh
    const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
    storedFormIds.push(formId);
    sessionStorage.setItem('stockLockFormIds', JSON.stringify(storedFormIds));
    
    return formId;
  };

  useEffect(() => {
    if (hasPermission) {
      // Clear form and entity items on page load/refresh
      // IMPORTANT: This runs on every mount (including refresh)
      
      // Cleanup stock locks on page load/refresh
      // Since the form is cleared, we should also clear any locks from previous session
      const cleanupLocksOnRefresh = async () => {
        try {
          const { cleanupStockSelectionLocks, deleteStockSelectionLock } = await import('../components/Api/GmsStockSelectionLock');
          
          // 1. Clean up any form IDs stored in sessionStorage (from previous session before refresh)
          const storedFormIds = JSON.parse(sessionStorage.getItem('stockLockFormIds') || '[]');
          for (const formId of storedFormIds) {
            try {
              await deleteStockSelectionLock(formId);
            } catch (err) {
              // Silently handle cleanup errors
            }
          }
          // Clear the stored form IDs
          sessionStorage.removeItem('stockLockFormIds');
          
          // 2. Also clean up locks older than 5 minutes (very short time to catch any missed ones)
          // This ensures any locks from the previous session are cleared
          await cleanupStockSelectionLocks(0.083).catch(() => {}); // 0.083 hours = 5 minutes
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
      cleanupLocksOnRefresh();
      
      setForm({
        ...defaultForm,
        Invoice_Date: new Date(),
        entityItems: [] // Explicitly clear entity items
      });
      setIsEditing(false);
      setIsReturnMode(false);
      setReturnSalesInvoiceId(null);
      setOriginalInvoiceNo(null);
      setTemporaryFormUniqueId(null);
      
      loadInitialData();
      loadInvoices();
      loadNextInvoiceNumber();
      
      // Check if current date is closed/holiday
      checkCurrentDateDayClosing();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, []); // Empty dependency array - run only on mount/refresh

  // Check if current date is closed/holiday and previous day closing
  const checkCurrentDateDayClosing = async () => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userBusinessId = authUser.businessId || user.user_Business_Id || null;
      const userBranchId = authUser.branchId || user.user_Branch_Id || null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Only check if we have business info (from session or form)
      const businessId = userBusinessId || form.Invoice_BusinessId;
      const branchId = userBranchId || form.Invoice_BranchId || null;
      
      if (businessId) {
        // Check current date
        const checkRequest = {
          transactionDate: formatDateForAPI(today, false),
          businessId: businessId,
          branchId: branchId
        };
        
        const dayClosingCheck = await checkDayClosing(checkRequest);
        
        if (dayClosingCheck.isClosed) {
          const message = dayClosingCheck.isHoliday 
            ? "Holiday has been marked for today. Please come back tomorrow."
            : "Day closing has been marked for today. Please come back tomorrow.";
          
          Swal.fire({
            icon: 'warning',
            title: 'Day Closed',
            text: message,
            confirmButtonText: 'OK'
          });
          
          // Disable the form
          setIsReadOnly(true);
          return;
        }
        
        // Check previous day closing - only if there are existing day closing records (not first time use)
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        const previousDayCheck = await checkDayClosing({
          transactionDate: formatDateForAPI(yesterday, false),
          businessId: businessId,
          branchId: branchId
        });
        
        // Only validate previous day closing if there are existing day closing records
        // If hasAnyDayClosing is false, it means first time use, so skip validation
        if (!previousDayCheck.isClosed && previousDayCheck.hasAnyDayClosing !== false) {
          const yesterdayStr = formatDateForDisplay(yesterday, 'date');
          Swal.fire({
            icon: 'warning',
            title: 'Previous Day Not Closed',
            text: `Day closing is not done for ${yesterdayStr}. Please close the previous day first before creating any transactions.`,
            confirmButtonText: 'OK'
          });
          
          // Disable the form
          setIsReadOnly(true);
        }
      }
    } catch (error) {
      console.error("Error checking day closing on page load:", error);
      // Don't block page load if check fails
    }
  };

  // Auto-populate company if user has companyId
  useEffect(() => {
    const userCompanyId = localStorage.getItem("userCompanyId");
    if (userCompanyId && businesses.length > 0) {
      const companyExists = businesses.find(b => b.business_Id === parseInt(userCompanyId));
      if (companyExists) {
        setForm(prev => ({
          ...prev,
          Invoice_BusinessId: parseInt(userCompanyId)
        }));
      }
    }
  }, [businesses]);

  // Re-check day closing when business/branch becomes available
  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userBusinessId = authUser.businessId || user.user_Business_Id || null;
    const businessId = userBusinessId || form.Invoice_BusinessId;
    
    // Only check if we have business info and haven't already disabled the form
    if (businessId && !isReadOnly) {
      // Small delay to ensure state is updated
      const timer = setTimeout(() => {
        checkCurrentDateDayClosing();
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [form.Invoice_BusinessId, form.Invoice_BranchId, businesses.length]);

  // Load customer advance balance and outstanding credit when customer is selected
  useEffect(() => {
    const loadCustomerInfo = async () => {
      if (form.Invoice_CustomerId) {
        try {
          setLoadingCustomerInfo(true);
          const [advanceBalance, outstandingCredit] = await Promise.all([
            getCustomerAdvanceBalance(form.Invoice_CustomerId),
            getCustomerOutstandingCredit(form.Invoice_CustomerId)
          ]);
          
          // Only set if values exist and are greater than 0
          const advanceValue = advanceBalance?.availableAdvance || advanceBalance?.AvailableAdvance || 0;
          const outstandingValue = outstandingCredit?.totalOutstanding || outstandingCredit?.TotalOutstanding || 0;
          
          setCustomerAdvanceBalance(advanceValue > 0 ? advanceBalance : null);
          setCustomerOutstandingCredit(outstandingValue > 0 ? outstandingCredit : null);
        } catch (error) {
          console.error("Error loading customer info:", error);
          setCustomerAdvanceBalance(null);
          setCustomerOutstandingCredit(null);
        } finally {
          setLoadingCustomerInfo(false);
        }
      } else {
        // Reset when customer is cleared
        setCustomerAdvanceBalance(null);
        setCustomerOutstandingCredit(null);
      }
    };

    loadCustomerInfo();
  }, [form.Invoice_CustomerId]);

  // Handle navigation state for read-only invoice view
  useEffect(() => {
    if (location.state?.viewInvoice && location.state?.isReadOnly) {
      const invoice = location.state.viewInvoice;
      setIsReadOnly(true);
      setActiveTab("1"); // Switch to Add Invoice tab
      
      // Map invoice details to entity items
      const entityItems = [];
      const productQuantities = {};
      const serviceQuantities = {};
      const packageQuantities = {};
      const packageSeriesQuantities = {};
      const productPrices = {};
      const servicePrices = {};
      const packagePrices = {};
      const packageSeriesPrices = {};
      const productDiscounts = {};
      const serviceDiscounts = {};
      const packageDiscounts = {};
      const packageSeriesDiscounts = {};
      const productTaxes = {};
      const serviceTaxes = {};
      const packageTaxes = {};
      const packageSeriesTaxes = {};
      
      if (invoice.details && Array.isArray(invoice.details) && invoice.details.length > 0) {
        invoice.details.forEach((detail, index) => {
          // Add safety checks for detail object
          if (!detail || typeof detail !== 'object') return;
          
          const entityType = detail.entityType || '';
          const entityId = detail.entityId || 0;
          
          // Find entity name from loaded data
          let entityName = 'Unknown Item';
          if (entityType === 'pro' && products.length > 0) {
            const product = products.find(p => p.product_Id === entityId);
            entityName = product?.product_NameEn || 'Unknown Product';
          } else if (entityType === 'ser' && services.length > 0) {
            const service = services.find(s => s.spaService_Id === entityId);
            entityName = service?.spaServiceName_En || 'Unknown Service';
          } else if ((entityType === 'pkg' || entityType === 'pac') && packages.length > 0) {
            const pkg = packages.find(p => p.packageId === entityId);
            entityName = pkg?.packageName_En || 'Unknown Package';
          } else if (entityType === 'sep' && packageSeries.length > 0) {
            const series = packageSeries.find(s => s.seriesId === entityId);
            entityName = series?.seriesName_En || 'Unknown Package Series';
          }
          
          // Calculate total tax percentage for multiple taxes
          let totalTaxPercent = 0;
          if (detail.taxPercent) {
            const taxPercentages = detail.taxPercent.split(',').map(tax => parseFloat(tax) || 0);
            totalTaxPercent = taxPercentages.reduce((sum, tax) => sum + tax, 0);
          }
          
          // Create entity item object with safety checks
          const entityItem = {
            id: index + 1,
            entityId: entityId,
            entityType: entityType,
            type: entityType === 'pro' ? 'product' : entityType === 'ser' ? 'service' : entityType === 'sep' ? 'packageSeries' : 'package',
            entityName: entityName,
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            price: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: totalTaxPercent,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            cost: detail.cost || 0,
            m_InstallmentId: detail.m_InstallmentId || null
          };
          
          entityItems.push(entityItem);
          
          // Set quantities, prices, discounts, and taxes with safety checks
          if (entityType === 'pro' && entityId) {
            productQuantities[entityId] = detail.quantity || 0;
            productPrices[entityId] = detail.price || 0;
            productDiscounts[entityId] = detail.disAmount || 0;
            productTaxes[entityId] = detail.taxAmt || 0;
          } else if (entityType === 'ser' && entityId) {
            serviceQuantities[entityId] = detail.quantity || 0;
            servicePrices[entityId] = detail.price || 0;
            serviceDiscounts[entityId] = detail.disAmount || 0;
            serviceTaxes[entityId] = detail.taxAmt || 0;
          } else if ((entityType === 'pkg' || entityType === 'pac') && entityId) {
            packageQuantities[entityId] = detail.quantity || 0;
            packagePrices[entityId] = detail.price || 0;
            packageDiscounts[entityId] = detail.disAmount || 0;
            packageTaxes[entityId] = detail.taxAmt || 0;
          } else if (entityType === 'sep' && entityId) {
            packageSeriesQuantities[entityId] = detail.quantity || 0;
            packageSeriesPrices[entityId] = detail.price || 0;
            packageSeriesDiscounts[entityId] = detail.disAmount || 0;
            packageSeriesTaxes[entityId] = detail.taxAmt || 0;
          }
        });
      }
      
      // Populate form with invoice data
      const populatedForm = {
        ...defaultForm,
        Invoice_Id: invoice.invoice_Id,
        Invoice_Date: new Date(invoice.invoice_Date),
        Invoice_CustomerId: invoice.invoice_CustomerId,
        Invoice_BusinessId: invoice.invoice_BusinessId,
        Invoice_BranchId: invoice.invoice_BranchId,
        Invoice_TotalQty: invoice.invoice_TotalQty,
        Invoice_TotalAmount: invoice.invoice_TotalAmount,
        Invoice_DisAmount: invoice.invoice_DisAmount,
        Invoice_TaxAmount: invoice.invoice_TaxAmount,
        Invoice_NetAmount: invoice.invoice_NetAmount,
        Invoice_Type: invoice.invoice_Type,
        PaidAmount: invoice.paidAmount,
        CashAmount: invoice.cashAmount,
        CashlessAmount: invoice.cashlessAmount,
        PaymentMethod: invoice.paymentMethod,
        AuthCode: invoice.authCode,
        CostAmount: invoice.costAmount,
        TotalCost: invoice.totalCost,
        // Add entity items and related data
        entityItems: entityItems,
        productQuantities: productQuantities,
        serviceQuantities: serviceQuantities,
        packageQuantities: packageQuantities,
        packageSeriesQuantities: packageSeriesQuantities,
        productPrices: productPrices,
        servicePrices: servicePrices,
        packagePrices: packagePrices,
        packageSeriesPrices: packageSeriesPrices,
        productDiscounts: productDiscounts,
        serviceDiscounts: serviceDiscounts,
        packageDiscounts: packageDiscounts,
        packageSeriesDiscounts: packageSeriesDiscounts,
        productTaxes: productTaxes,
        serviceTaxes: serviceTaxes,
        packageTaxes: packageTaxes,
        packageSeriesTaxes: packageSeriesTaxes,
      };
      
      setForm(populatedForm);
      
      // Clear the navigation state to prevent re-population on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, products, services, packages, packageSeries]);

  // Handle navigation from booking page
  useEffect(() => {
    const processBookingData = async () => {
      
      if (location.state?.customerId && location.state?.bookingId && customers.length > 0 && services.length > 0 && packages.length > 0 && packageSeries.length > 0) {
      // Auto-select customer when navigated from booking
      const customerId = location.state.customerId;
      const bookingDetails = location.state.bookingDetails || [];
      
      const customer = customers.find(c => c.customer_Id == customerId);
      
      if (customer) {
        
        // Check if booking has membership entity type
        const hasMembershipEntity = bookingDetails.some(detail => detail.entity_Type === 'mem');
        
        // Get membership-related entity IDs if membership exists
        let membershipEntityIds = new Set();
        if (hasMembershipEntity) {
          try {
            const memberships = await getCustomerMemberships({ customerId: customerId });
            if (memberships && memberships.length > 0) {
              memberships.forEach(membership => {
                // Add the main entity (package/series)
                membershipEntityIds.add(membership.entity_Id);
                
                // Add all services and packages from membership details
                if (membership.details) {
                  membership.details.forEach(detail => {
                    if (detail.serviceId) membershipEntityIds.add(detail.serviceId);
                    if (detail.packageId) membershipEntityIds.add(detail.packageId);
                  });
                }
              });
            }
          } catch (error) {
            console.error('Error fetching membership data:', error);
          }
        }
        
        // Process booking details to populate entities
        const entityItems = [];
        const selectedServices = [];
        const selectedPackages = [];
        const selectedPackageSeries = [];
        
        // Filter out membership-related entities from regular billing
        const standaloneDetails = bookingDetails.filter(detail => {
          // Keep standalone entities (not membership-related)
          // Only filter out if it's actually a membership entity type, not just because the ID appears in membership data
          return detail.entity_Type !== 'mem';
        });
        
        standaloneDetails.forEach(detail => {
          if (detail.entity_Type === 'ser') {
            // Service
            const service = services.find(s => s.spaService_Id === detail.entity_Id);
            if (service) {
              selectedServices.push(service);
              // Calculate tax percentage from taxIds
              let taxPercentage = 0;
              if (service.taxIds && service.taxIds.length > 0) {
                const tax = taxes.find(t => service.taxIds.includes(t.tax_Id));
                taxPercentage = tax ? tax.tax_Perc : 0;
              }
              
              const unitPrice = service.amount || 0;
              const discount = service.discountPercentage || 0;
              const totalPrice = 1 * unitPrice;
              const discountAmount = (unitPrice * discount / 100) * 1;
              const taxAmount = ((totalPrice - discountAmount) * taxPercentage / 100);
              const netAmount = totalPrice - discountAmount + taxAmount;
              
              entityItems.push({
                id: `ser_${detail.entity_Id}`,
                entityId: detail.entity_Id,
                entityType: 'ser',
                type: 'service',
                entityName: service.spaServiceName_En,
                quantity: 1,
                unitPrice: unitPrice,
                discount: discount,
                discountAmount: discountAmount,
                tax: taxPercentage,
                taxAmount: taxAmount,
                netAmount: netAmount,
                costPrice: service.cost || 0
              });
            }
          } else if (detail.entity_Type === 'pac') {
            // Package
            const pkg = packages.find(p => p.packageId === detail.entity_Id);
            if (pkg) {
              selectedPackages.push(pkg);
              // Calculate tax percentage from taxIds
              let taxPercentage = 0;
              if (pkg.taxIds && pkg.taxIds.length > 0) {
                const tax = taxes.find(t => pkg.taxIds.includes(t.tax_Id));
                taxPercentage = tax ? tax.tax_Perc : 0;
              }
              
              const unitPrice = pkg.amount || 0;
              const discount = pkg.discountPercentage || 0;
              const totalPrice = 1 * unitPrice;
              const discountAmount = (unitPrice * discount / 100) * 1;
              const taxAmount = ((totalPrice - discountAmount) * taxPercentage / 100);
              const netAmount = totalPrice - discountAmount + taxAmount;
              
              entityItems.push({
                id: `pac_${detail.entity_Id}`,
                entityId: detail.entity_Id,
                entityType: 'pac',
                type: 'package',
                entityName: pkg.packageName_En,
                quantity: 1,
                unitPrice: unitPrice,
                discount: discount,
                discountAmount: discountAmount,
                tax: taxPercentage,
                taxAmount: taxAmount,
                netAmount: netAmount,
                costPrice: pkg.cost || 0
              });
            }
          } else if (detail.entity_Type === 'sep') {
            // Package Series
            const series = packageSeries.find(s => s.seriesId === detail.entity_Id);
            if (series) {
              selectedPackageSeries.push(series);
              // Calculate tax percentage from taxIds
              let taxPercentage = 0;
              if (series.taxIds && series.taxIds.length > 0) {
                const tax = taxes.find(t => series.taxIds.includes(t.tax_Id));
                taxPercentage = tax ? tax.tax_Perc : 0;
              }
              
              const unitPrice = series.amount || 0;
              const discount = series.discountPercentage || 0;
              const totalPrice = 1 * unitPrice;
              const discountAmount = (unitPrice * discount / 100) * 1;
              const taxAmount = ((totalPrice - discountAmount) * taxPercentage / 100);
              const netAmount = totalPrice - discountAmount + taxAmount;
              
              entityItems.push({
                id: `sep_${detail.entity_Id}`,
                entityId: detail.entity_Id,
                entityType: 'sep',
                type: 'packageSeries',
                entityName: series.seriesName_En,
                quantity: 1,
                unitPrice: unitPrice,
                discount: discount,
                discountAmount: discountAmount,
                tax: taxPercentage,
                taxAmount: taxAmount,
                netAmount: netAmount,
                costPrice: series.cost || 0
              });
            }
          } else if (detail.entity_Type === 'mem') {
            // Membership - will be handled by membership installment logic
          }
        });
        
        setForm(prev => ({
          ...prev,
          Invoice_CustomerId: customerId,
          selectedServices: selectedServices,
          selectedPackages: selectedPackages,
          selectedPackageSeries: selectedPackageSeries,
          entityItems: entityItems
        }));
        
        // Switch to Add Invoice tab
        setActiveTab("1");
        
        // If membership entities exist, show membership modal after processing standalone entities
        if (hasMembershipEntity) {
          // Open membership modal after a short delay to ensure form is updated
          setTimeout(() => {
            openMembershipModal(customerId);
          }, 100);
        }
        
        // Clear the navigation state
        window.history.replaceState({}, document.title);
      }
    }
    };
    
    processBookingData();
  }, [location.state, customers, services, packages, packageSeries, taxes]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadInvoices();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCustomers(),
        loadBusinesses(),
        loadBranches(),
        loadProducts(),
        loadServices(),
        loadPackages(),
        loadPackageSeries(),
        loadTaxes(),
        loadEmployees(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({ status: true });
      setCustomers(data || []);
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };

  // Manual search function for customer search
  const searchCustomersManually = async (inputValue) => {
    
    if (!inputValue || inputValue.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      const response = await searchCustomers(inputValue, 1, 20);
      
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Manual search function for customer filter search
  const searchCustomersFilterManually = async (inputValue) => {
    
    if (!inputValue || inputValue.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      const response = await searchCustomers(inputValue, 1, 20);
      
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleCustomerAdded = async (newCustomer) => {
    
    // Get the customer ID from the response
    const customerId = newCustomer.customer_Id || newCustomer.id;
    
    try {
      // Try to fetch the specific customer by ID
      const customerData = await getCustomers({ customer_Id: customerId });
      
      if (customerData && customerData.length > 0) {
        const addedCustomer = customerData[0];
        
        // Set the newly added customer as selected
        setForm(prev => ({
          ...prev,
          Invoice_CustomerId: customerId
        }));
        // Set the selected customer option for display
        setSelectedCustomerOption({
          value: customerId,
          label: `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`
        });
        // Also reload the full customer list to keep it updated
        loadCustomers();
      } else {
        // Fallback: just set the ID and let the user search for the customer
        setForm(prev => ({
          ...prev,
          Invoice_CustomerId: customerId
        }));
        setSelectedCustomerOption({
          value: customerId,
          label: `Customer ID: ${customerId}`
        });
        
        // Still reload the full customer list
        loadCustomers();
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      // Fallback: just set the ID
      setForm(prev => ({
        ...prev,
        Invoice_CustomerId: customerId
      }));
      setSelectedCustomerOption({
        value: customerId,
        label: `Customer ID: ${customerId}`
      });
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ status: true });
      setBusinesses(data || []);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadEmployees = async () => {
    try {
      // Get companyId from login response
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const companyId = authUser.companyId;
      
      // Filter employees by company if companyId exists
      const filter = companyId ? { business_Id: companyId } : {};
      const data = await getEmployees(filter);
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadNextInvoiceNumber = async () => {
    try {
      // Use POST method with business ID for regular invoice number generation
      const businessId = form.Invoice_BusinessId || null;
      const payload = {
        Business_Id: businessId || 0,
        IsReturn: false,
        Return_Original_InvoiceNo: null
      };
      
      const response = await fetch('https://localhost:7235/api/SalesInvoice/get-next-invoice-number', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setForm(prev => {
        return { ...prev, Invoice_No: data.nextInvoiceNumber ? String(data.nextInvoiceNumber) : "" };
      });
    } catch (error) {
      console.error('Error loading next invoice number:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({ status: true });
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
    }
  };

  const loadServices = async () => {
    try {
      const data = await getSpaServices({ status: true });
      setServices(data || []);
    } catch (error) {
      console.error("Error loading services:", error);
    }
  };

  const loadPackages = async () => {
    try {
      const data = await getSpaPackages({ status: true });
      setPackages(data || []);
    } catch (error) {
      console.error("Error loading packages:", error);
    }
  };

  const loadPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({ status: true });
      setPackageSeries(data || []);
    } catch (error) {
      console.error("Error loading package series:", error);
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ status: true });
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
    }
  };

  // Function to fetch individual entity cost
  const fetchEntityCost = async (entityType, entityId) => {
    try {
      let cost = 0;
      
      if (entityType === 'product') {
        const product = await getProducts({ product_Id: entityId });
        if (product && product.length > 0) {
          cost = product[0].product_CostPrice || 0;
        }
      } else if (entityType === 'service') {
        const service = await getSpaServices({ SpaService_Id: entityId });
        if (service && service.length > 0) {
          cost = service[0].cost || 0;
        }
      } else if (entityType === 'package') {
        const packageData = await getSpaPackages({ PackageId: entityId });
        if (packageData && packageData.length > 0) {
          cost = packageData[0].cost || 0;
        }
      } else if (entityType === 'packageSeries') {
        const packageSeries = await getSpaPackageSeries({ SeriesId: entityId });
        if (packageSeries && packageSeries.length > 0) {
          cost = packageSeries[0].cost || 0;
        }
      }
      
      return cost;
    } catch (error) {
      console.error(`Error fetching ${entityType} cost:`, error);
      return 0;
    }
  };

  // Function to populate entity data when selected
  const populateEntityData = (entityType, selectedItems) => {
    selectedItems.forEach(item => {
      let entityData = null;
      
      if (entityType === 'product') {
        entityData = products.find(p => p.product_Id === item.value);
        if (entityData) {
          handlePriceChange('product', item.value, entityData.product_CostPrice || 0);
          handleDiscountChange('product', item.value, entityData.product_DiscountPercentage || 0);
          // Calculate tax percentage for products using combined tax calculation
          const taxPercentage = calculateCombinedTaxPercentage(entityData.taxIds || []);
          handleTaxChange('product', item.value, taxPercentage);
        }
      } else if (entityType === 'service') {
        entityData = services.find(s => s.spaService_Id === item.value);
        if (entityData) {
          // For services, we need to calculate price based on duration or use a default
          const servicePrice = entityData.amount || 100; // Default price if not available
          handlePriceChange('service', item.value, servicePrice);
          handleDiscountChange('service', item.value, entityData.discountPercentage || 0);
          // Calculate tax percentage for services using combined tax calculation
          const taxPercentage = calculateCombinedTaxPercentage(entityData.taxIds || []);
          handleTaxChange('service', item.value, taxPercentage);
        }
      } else if (entityType === 'package') {
        entityData = packages.find(p => p.packageId === item.value);
        if (entityData) {
          handlePriceChange('package', item.value, entityData.amount || 0);
          handleDiscountChange('package', item.value, entityData.discountPercentage || 0);
          // Calculate tax percentage for packages using combined tax calculation
          const taxPercentage = calculateCombinedTaxPercentage(entityData.taxIds || []);
          handleTaxChange('package', item.value, taxPercentage);
        }
      } else if (entityType === 'packageSeries') {
        entityData = packageSeries.find(s => s.seriesId === item.value);
        if (entityData) {
          handlePriceChange('packageSeries', item.value, entityData.amount || 0);
          handleDiscountChange('packageSeries', item.value, entityData.discountPercentage || 0);
          // Calculate tax percentage for package series using combined tax calculation
          const taxPercentage = calculateCombinedTaxPercentage(entityData.taxIds || []);
          handleTaxChange('packageSeries', item.value, taxPercentage);
        }
      }
    });
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getSalesInvoices(filter);
      
      // Sort invoices by invoice ID in descending order (newest first)
      const sortedData = (data || []).sort((a, b) => {
        const idA = a.invoice_Id || a.Invoice_Id || 0;
        const idB = b.invoice_Id || b.Invoice_Id || 0;
        return idB - idA;
      });
      
      setInvoices(sortedData);
      setTotalCount(sortedData.length);
    } catch (error) {
      console.error("Error loading invoices:", error);
      Swal.fire("Error", "Failed to load invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals when form changes
  useEffect(() => {
    calculateTotals();
  }, [form.selectedProducts, form.selectedServices, form.selectedPackages, form.selectedPackageSeries, form.productQuantities, form.serviceQuantities, form.packageQuantities, form.packageSeriesQuantities, form.discountPercentage, form.overallDiscountPercentage, form.entityItems]);

  const calculateTotals = () => {
    let totalQty = 0;
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    let totalCost = 0;

    // Calculate totals from entityItems array
    form.entityItems.forEach(item => {
      const lineTotal = (item.unitPrice ?? 0) * item.quantity;
      const discountAmount = item.discountAmount || 0;
      const taxAmount = item.taxAmount || 0;
      const costAmount = (item.costPrice || 0) * item.quantity; // Cost * quantity
      
      totalQty += item.quantity;
      totalAmount += lineTotal;
      totalDiscount += discountAmount;
      totalTax += taxAmount;
      totalCost += costAmount;
    });

    // Apply overall discount percentage
    const overallDiscountAmount = (totalAmount * (form.discountPercentage || 0)) / 100;
    const finalTotalDiscount = totalDiscount + overallDiscountAmount;

    // Calculate initial net amount: amount - discount + tax
    const initialNetAmount = totalAmount - finalTotalDiscount + totalTax;

    // Apply additional overall discount percentage to initial net amount
    const additionalOverallDiscountAmount = (initialNetAmount * (form.overallDiscountPercentage || 0)) / 100;
    const finalNetAmount = initialNetAmount - additionalOverallDiscountAmount;

    setForm(prev => ({
      ...prev,
      Invoice_TotalQty: totalQty,
      Invoice_TotalAmount: totalAmount,
      Invoice_DisAmount: finalTotalDiscount,
      Invoice_TaxAmount: totalTax,
      Invoice_NetAmount: finalNetAmount,
      CostAmount: totalCost,
      TotalCost: totalCost,
      overallDiscountAmount: additionalOverallDiscountAmount,
    }));
  };

  const handleSave = async () => {
    try {
      // Check day closing before saving
      if (form.Invoice_BusinessId && form.Invoice_Date) {
        try {
          const invoiceDate = new Date(form.Invoice_Date);
          const checkRequest = {
            transactionDate: formatDateForAPI(invoiceDate, false),
            businessId: form.Invoice_BusinessId,
            branchId: form.Invoice_BranchId || null
          };
          
          const dayClosingCheck = await checkDayClosing(checkRequest);
          
          if (dayClosingCheck.isClosed) {
            const message = dayClosingCheck.isHoliday 
              ? "Holiday has been marked for this date. Please come back tomorrow."
              : "Day closing has been marked for this date. Please come back tomorrow.";
            Swal.fire("Day Closed", message, "warning");
            return;
          }
          
          // Check previous day closing - always check if creating for current or future date
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const invoiceDateOnly = new Date(invoiceDate);
          invoiceDateOnly.setHours(0, 0, 0, 0);
          
          // If invoice date is today or future, check if previous day is closed
          // Only validate if there are existing day closing records (not first time use)
          if (invoiceDateOnly >= today) {
            const previousDate = new Date(invoiceDateOnly);
            previousDate.setDate(previousDate.getDate() - 1);
            
            const previousDayCheck = await checkDayClosing({
              transactionDate: formatDateForAPI(previousDate, false),
              businessId: form.Invoice_BusinessId,
              branchId: form.Invoice_BranchId || null
            });
            
            // Only validate previous day closing if there are existing day closing records
            // If hasAnyDayClosing is false, it means first time use, so skip validation
            if (!previousDayCheck.isClosed && previousDayCheck.hasAnyDayClosing !== false) {
              Swal.fire("Previous Day Not Closed", `Day closing is not done for ${formatDateForDisplay(previousDate, 'date')}. Please close the previous day first.`, "warning");
              return;
            }
          }
        } catch (error) {
          console.error("Error checking day closing:", error);
          // Continue with save if check fails (don't block due to API error)
        }
      }

      // Check for validation errors in entity items
      const itemsWithErrors = form.entityItems.filter(item => item.validationError);
      
      if (itemsWithErrors.length > 0) {
        const errorMessages = itemsWithErrors.map(item => `${item.entityName}: ${item.validationError}`).join('\n');
        Swal.fire("Validation Error", `Please fix the validation errors in the selected items:\n\n${errorMessages}`, "error");
        return;
      }

      // Check for missing salesman for products (skip in return mode as salesman comes from original)
      if (!isReturnMode) {
        const productsWithoutSalesman = form.entityItems.filter(item => item.type === 'product' && !item.salesmanId);
        
        if (productsWithoutSalesman.length > 0) {
          const productNames = productsWithoutSalesman.map(item => item.entityName).join(', ');
          Swal.fire("Validation Error", `Please select salesman for the following products:\n\n${productNames}`, "error");
          return;
        }
      }
      
      // Check stock locks before save (if form has products with stock selection)
      if (temporaryFormUniqueId) {
        // Collect all stock IDs from entityItems (for return mode) and selectedStockData (for regular sales)
        const allStockIds = [];
        
        // Get stock IDs from entityItems (return mode)
        form.entityItems.forEach(item => {
          if (item.type === 'product' && item.stockData) {
            const stockIds = item.stockData.split(',').filter(id => id.trim() !== '');
            allStockIds.push(...stockIds);
          }
        });
        
        // Get stock IDs from selectedStockData (regular sales mode)
        Object.values(form.selectedStockData).forEach(stockDataArray => {
          if (Array.isArray(stockDataArray)) {
            stockDataArray.forEach(group => {
              if (group.stocks && Array.isArray(group.stocks)) {
                group.stocks.forEach(stock => {
                  const stockId = stock.purchaseStock_Id || stock.stockTransaction_StockId;
                  if (stockId) {
                    allStockIds.push(stockId.toString());
                  }
                });
              }
            });
          }
        });
        
        if (allStockIds.length > 0) {
          try {
            const checkResult = await checkStockSelectionLocks(
              allStockIds.join(','),
              temporaryFormUniqueId
            );
            
            if (!checkResult.isSuccess || checkResult.hasLocks) {
              // Stocks are locked, prevent save and refresh stock modal
              Swal.fire({
                title: "Stock Locked",
                text: checkResult.message || "Some selected stocks are no longer available. Please refresh and select different stocks.",
                icon: "error",
                confirmButtonText: "OK"
              }).then(() => {
                // Refresh stock modal for the first product with stock
                const firstProductWithStock = form.entityItems.find(item => 
                  item.type === 'product' && (item.stockData || form.selectedStockData[item.entityId])
                );
                if (firstProductWithStock) {
                  handleOpenStockModal(
                    firstProductWithStock.entityId,
                    firstProductWithStock.entityName,
                    firstProductWithStock.quantity,
                    form.entityItems.findIndex(i => i.id === firstProductWithStock.id)
                  );
                }
              });
              setSaveLoading(false);
              return;
            }
          } catch (error) {
            console.error('Error checking stock locks:', error);
            // Continue with save if check fails (don't block user)
          }
        }
      }
      
      setSaveLoading(true);
      const session = getUserSession();

      // Prepare invoice details from entityItems array
      const details = [];
      
      // Add details from entityItems array (new approach)
      form.entityItems.forEach((item, index) => {
        const entityType = item.type === 'product' ? 'pro' : item.type === 'service' ? 'ser' : item.type === 'packageSeries' ? 'sep' : 'pac';
        const tempDetailId = index + 1;
        const detailRow = {
          Detail_Id: 0,
          EntityId: item.entityId ? parseInt(item.entityId) : 0,
          EntityType: entityType,
          Quantity: item.quantity,
          Price: item.unitPrice ?? 0, // Unit price (editable) - convert null to 0 for API
          DisPer: item.discount ?? 0, // Discount - convert null to 0 for API
          DisAmount: item.discountAmount || 0, // Use calculated discount amount
          TaxAmt: item.taxAmount || 0,
          NetAmount: item.netAmount || 0,
          Cost: item.costPrice || 0, // Cost price from default entity data
          M_InstallmentId: item.m_InstallmentId ? parseInt(item.m_InstallmentId) : null, // Add membership installment ID
          Salesman_ID: item.salesmanId ? parseInt(item.salesmanId) : 0, // Add salesman ID
          CreatedBy: parseInt(session.userId),
          CreatedOn: new Date(),
          ModifiedBy: null,
          ModifiedOn: null,
          StockData: item.stockData ? String(item.stockData) : null, // Add stock data for products
          TempDetailId: tempDetailId,
        };
        details.push(detailRow);
      });

      // Prepare tax details from entityItems array
      const taxes = [];
      
      // Add taxes from entityItems (new approach)
      form.entityItems.forEach(item => {
        // Add tax records for items with valid tax IDs and tax > 0
        if (item.tax > 0 && item.taxIds && item.taxIds.length > 0) {
          // Add each tax ID separately with its individual percentage
          item.taxBreakdown.forEach(taxBreakdown => {
            
            taxes.push({
              Det_Id: 0,
              Tax_Id: taxBreakdown.id,
              TaxPer: taxBreakdown.percentage,
              CreatedBy: parseInt(session.userId),
              CreatedOn: new Date(),
              ModifiedBy: null,
              ModifiedOn: null,
            });
          });
        } else if (item.tax > 0 && (!item.taxIds || item.taxIds.length === 0)) {
          console.warn(`Skipping tax for ${item.type} ${item.entityName}: No valid Tax IDs found (tax% ${item.tax})`);
        }
      });

      // For return mode, validate stock selection
      if (isReturnMode) {
        const productsWithoutStock = form.entityItems.filter(
          item => item.type === 'product' && (!item.stockData || item.stockData === '')
        );
        if (productsWithoutStock.length > 0) {
          Swal.fire("Error", "Please select stock items for all products being returned.", "error").then(() => {
            // Automatically open stock selection modal for the first product without stock
            const firstProductWithoutStock = productsWithoutStock[0];
            if (firstProductWithoutStock) {
              const itemIndex = form.entityItems.findIndex(i => i.id === firstProductWithoutStock.id);
              if (itemIndex !== -1) {
                handleOpenStockModal(
                  firstProductWithoutStock.entityId,
                  firstProductWithoutStock.entityName,
                  firstProductWithoutStock.quantity,
                  itemIndex
                );
              }
            }
          });
          setSaveLoading(false);
          return;
        }
        
        // Validate Cash or Cashless amount for return mode
        const cashAmount = form.CashAmount ?? 0;
        const cashlessAmount = form.CashlessAmount ?? 0;
        const hasCashAmount = cashAmount !== null && cashAmount !== '' && parseFloat(cashAmount) > 0;
        const hasCashlessAmount = cashlessAmount !== null && cashlessAmount !== '' && parseFloat(cashlessAmount) > 0;
        
        if (!hasCashAmount && !hasCashlessAmount) {
          Swal.fire("Error", "Please enter either Cash Amount or Cashless Amount for the sales return.", "error");
          setSaveLoading(false);
          return;
        }
      }

      // Validate cash/cashless amounts for cash invoice type
      if (form.Invoice_Type === "ca" && form.Invoice_NetAmount > 0) {
        const cashAmount = form.CashAmount == null || form.CashAmount === '' ? 0 : (parseFloat(form.CashAmount) || 0);
        const cashlessAmount = form.CashlessAmount == null || form.CashlessAmount === '' ? 0 : (parseFloat(form.CashlessAmount) || 0);
        const totalPayment = cashAmount + cashlessAmount;
        const netAmount = parseFloat(form.Invoice_NetAmount) || 0;
        
        // Round to 2 decimal places for comparison
        const roundedTotalPayment = Math.round(totalPayment * 100) / 100;
        const roundedNetAmount = Math.round(netAmount * 100) / 100;
        
        if (roundedTotalPayment !== roundedNetAmount) {
          Swal.fire({
            icon: "error",
            title: "Error",
            html: `Cash Amount + Cashless Amount must equal Net Amount.<br/><br/>
                   <strong>Net Amount:</strong> ${netAmount.toFixed(2)}<br/>
                   <strong>Cash Amount:</strong> ${cashAmount.toFixed(2)}<br/>
                   <strong>Cashless Amount:</strong> ${cashlessAmount.toFixed(2)}<br/>
                   <strong>Total Payment:</strong> ${totalPayment.toFixed(2)}`,
            confirmButtonText: "OK"
          });
          setSaveLoading(false);
          return;
        }
        
        // Show confirmation dialog with amounts
        const confirmResult = await Swal.fire({
          icon: "question",
          title: "Confirm Invoice Save",
          html: `Please confirm the invoice details:<br/><br/>
                 <strong>Total Invoice Amount:</strong> ${netAmount.toFixed(2)}<br/>
                 <strong>Cash Amount:</strong> ${cashAmount.toFixed(2)}<br/>
                 <strong>Cashless Amount:</strong> ${cashlessAmount.toFixed(2)}<br/>
                 <strong>Total Payment:</strong> ${totalPayment.toFixed(2)}`,
          showCancelButton: true,
          confirmButtonText: "Confirm & Save",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#556ee6",
          cancelButtonColor: "#6c757d"
        });
        
        if (!confirmResult.isConfirmed) {
          setSaveLoading(false);
          return;
        }
      }

      // Combine selected date with current time (client's local timezone)
      // This ensures the time reflects when the user is creating the invoice in their timezone
      const selectedDate = form.Invoice_Date instanceof Date ? form.Invoice_Date : new Date(form.Invoice_Date);
      const now = new Date();
      const invoiceDateTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );

      const saveData = {
        Invoice_Id: form.Invoice_Id,
        Invoice_No: form.Invoice_No ? String(form.Invoice_No) : "", // Convert to string (empty string for new invoices, will be generated by backend)
        Invoice_Date: formatDateForAPI(invoiceDateTime, true), // Selected date + current time (client timezone)
        Invoice_DueDate: null,
        Invoice_CustomerId: form.Invoice_CustomerId,
        Invoice_BusinessId: form.Invoice_BusinessId,
        Invoice_BranchId: form.Invoice_BranchId,
        Invoice_TotalQty: form.Invoice_TotalQty,
        Invoice_TotalAmount: form.Invoice_TotalAmount,
        Invoice_DisAmount: form.Invoice_DisAmount,
        Invoice_TaxAmount: form.Invoice_TaxAmount,
        Invoice_NetAmount: form.Invoice_NetAmount,
        Invoice_Type: form.Invoice_Type,
        PaidAmount: isReturnMode ? form.Invoice_NetAmount : (form.Invoice_Type === "ca" ? form.Invoice_NetAmount : 0),
        CashAmount: form.CashAmount == null || form.CashAmount === '' ? 0 : (parseFloat(form.CashAmount) || 0),
        CashlessAmount: form.CashlessAmount == null || form.CashlessAmount === '' ? 0 : (parseFloat(form.CashlessAmount) || 0),
        PaymentMethod: form.PaymentMethod,
        AuthCode: form.AuthCode,
        CostAmount: form.CostAmount,
        TotalCost: form.TotalCost,
        OverallDiscountPercentage: form.overallDiscountPercentage ?? 0,
        OverallDiscountAmount: form.overallDiscountAmount,
        CreatedBy: parseInt(session.userId),
        ModifiedBy: null,
        CreatedDate: new Date(),
        ModifiedDate: null,
        Details: details,
        Taxes: taxes,
        // Return fields
        IsReturn: isReturnMode ? true : false,
        Return_Sales_Invoice_Id: isReturnMode ? returnSalesInvoiceId : null,
        // Include Booking_Id when navigated from Booking page, so backend can link it
        Booking_Id: location.state?.bookingId || null,
      };

      const result = await saveSalesInvoice(saveData);
      
      // Handle the response object
      const isSuccess = result?.isSuccess || false;
      const message = result?.message || "Failed to save invoice";
      const returnedId = result?.returnedId || 0;
      
      if (isSuccess) {
        // Cleanup stock locks after successful save
        if (temporaryFormUniqueId) {
          try {
            await deleteStockSelectionLock(temporaryFormUniqueId);
          } catch (error) {
            console.error('Error cleaning up stock locks after save:', error);
          }
        }
        
        Swal.fire("Success", isReturnMode ? "Sales return created successfully!" : "Invoice saved successfully!", "success").then(async () => {
          resetForm();
          loadInvoices();
          loadNextInvoiceNumber(); // Generate new invoice number
          window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top after save
          
          // Automatically open print modal after save (only for regular sales invoices, not sales returns)
          if (!isReturnMode && returnedId && returnedId > 0) {
            try {
              setPrintLoading(true);
              // Fetch invoice data for print using the same function as handlePrintInvoice
              const invoiceData = await getInvoiceForPrint(returnedId);
              
              if (invoiceData && invoiceData.header) {
                setInvoicePrintData(invoiceData);
                setShowInvoicePrintModal(true);
              }
              setPrintLoading(false);
            } catch (error) {
              console.error("Error loading invoice for print after save:", error);
              setPrintLoading(false);
              // Don't show error to user as save was successful, just log it
            }
          }
        });
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      Swal.fire("Error", "Failed to save invoice", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = async (item) => {
    // Helper function to get entity name
    const getEntityName = (entityType, entityId) => {
      if (entityType === 'pro') {
        const product = products.find(p => p.product_Id === entityId);
        return product ? product.product_NameEn : `Product ID: ${entityId}`;
      } else if (entityType === 'ser') {
        const service = services.find(s => s.spaService_Id === entityId);
        return service ? service.spaServiceName_En : `Service ID: ${entityId}`;
      } else if (entityType === 'pac') {
        const pkg = packages.find(p => p.packageId === entityId);
        return pkg ? pkg.packageName_En : `Package ID: ${entityId}`;
      } else if (entityType === 'sep') {
        const series = packageSeries.find(s => s.seriesId === entityId);
        return series ? series.seriesName_En : `Package Series ID: ${entityId}`;
      }
      return `Entity ID: ${entityId}`;
    };

    // Process invoice details to populate form arrays
    const selectedProducts = [];
    const selectedServices = [];
    const selectedPackages = [];
    const selectedPackageSeries = [];
    const productQuantities = {};
    const serviceQuantities = {};
    const packageQuantities = {};
    const packageSeriesQuantities = {};
    const productPrices = {};
    const servicePrices = {};
    const packagePrices = {};
    const packageSeriesPrices = {};
    const productDiscounts = {};
    const serviceDiscounts = {};
    const packageDiscounts = {};
    const packageSeriesDiscounts = {};
    const productTaxes = {};
    const serviceTaxes = {};
    const packageTaxes = {};
    const packageSeriesTaxes = {};
    const entityItems = [];

    if (item.details && item.details.length > 0) {
      item.details.forEach(detail => {
        if (detail.entityType === 'pro') {
          selectedProducts.push(detail.entityId);
          productQuantities[detail.entityId] = detail.quantity || 0;
          productPrices[detail.entityId] = detail.price || 0;
          productDiscounts[detail.entityId] = detail.disPer || 0;
          productTaxes[detail.entityId] = detail.taxAmt || 0;
          entityItems.push({
            id: detail.entityId,
            entityId: detail.entityId,
            type: 'product',
            entityType: 'pro',
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: detail.taxAmt || 0,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            entityName: getEntityName('pro', detail.entityId),
            m_InstallmentId: detail.m_InstallmentId || null
          });
        } else if (detail.entityType === 'ser') {
          selectedServices.push(detail.entityId);
          serviceQuantities[detail.entityId] = detail.quantity || 0;
          servicePrices[detail.entityId] = detail.price || 0;
          serviceDiscounts[detail.entityId] = detail.disPer || 0;
          serviceTaxes[detail.entityId] = detail.taxAmt || 0;
          entityItems.push({
            id: detail.entityId,
            entityId: detail.entityId,
            type: 'service',
            entityType: 'ser',
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: detail.taxAmt || 0,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            entityName: getEntityName('ser', detail.entityId),
            m_InstallmentId: detail.m_InstallmentId || null
          });
        } else if (detail.entityType === 'pac') {
          selectedPackages.push(detail.entityId);
          packageQuantities[detail.entityId] = detail.quantity || 0;
          packagePrices[detail.entityId] = detail.price || 0;
          packageDiscounts[detail.entityId] = detail.disPer || 0;
          packageTaxes[detail.entityId] = detail.taxAmt || 0;
          entityItems.push({
            id: detail.entityId,
            entityId: detail.entityId,
            type: 'package',
            entityType: 'pac',
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: detail.taxAmt || 0,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            entityName: getEntityName('pac', detail.entityId),
            m_InstallmentId: detail.m_InstallmentId || null
          });
        } else if (detail.entityType === 'sep') {
          selectedPackageSeries.push(detail.entityId);
          packageSeriesQuantities[detail.entityId] = detail.quantity || 0;
          packageSeriesPrices[detail.entityId] = detail.price || 0;
          packageSeriesDiscounts[detail.entityId] = detail.disPer || 0;
          packageSeriesTaxes[detail.entityId] = detail.taxAmt || 0;
          entityItems.push({
            id: detail.entityId,
            entityId: detail.entityId,
            type: 'packageSeries',
            entityType: 'sep',
            quantity: detail.quantity || 0,
            unitPrice: detail.price || 0,
            discount: detail.disPer || 0,
            discountAmount: detail.disAmount || 0,
            tax: detail.taxAmt || 0,
            taxAmount: detail.taxAmt || 0,
            netAmount: detail.netAmount || 0,
            entityName: getEntityName('sep', detail.entityId),
            m_InstallmentId: detail.m_InstallmentId || null
          });
        }
      });
    }

    setForm({
      ...item,
      Invoice_Id: item.invoice_Id || item.Invoice_Id,
      Invoice_No: (item.invoice_No || item.Invoice_No) ? String(item.invoice_No || item.Invoice_No) : "",
      Invoice_Date: new Date(item.invoice_Date || item.Invoice_Date),
      Invoice_CustomerId: item.invoice_CustomerId || item.Invoice_CustomerId,
      Invoice_BusinessId: item.invoice_BusinessId || item.Invoice_BusinessId,
      Invoice_BranchId: item.invoice_BranchId || item.Invoice_BranchId,
      Invoice_TotalQty: item.invoice_TotalQty || item.Invoice_TotalQty,
      Invoice_TotalAmount: item.invoice_TotalAmount || item.Invoice_TotalAmount,
      Invoice_DisAmount: item.invoice_DisAmount || item.Invoice_DisAmount,
      Invoice_TaxAmount: item.invoice_TaxAmount || item.Invoice_TaxAmount,
      Invoice_NetAmount: item.invoice_NetAmount || item.Invoice_NetAmount,
      Invoice_Type: item.invoice_Type || item.Invoice_Type,
      PaidAmount: item.paidAmount || item.PaidAmount,
      CashAmount: item.cashAmount || item.CashAmount,
      CashlessAmount: item.cashlessAmount || item.CashlessAmount,
      PaymentMethod: item.paymentMethod || item.PaymentMethod,
      AuthCode: item.authCode || item.AuthCode,
      CostAmount: item.costAmount || item.CostAmount,
      TotalCost: item.totalCost || item.TotalCost,
      selectedProducts,
      selectedServices,
              selectedPackages,
        selectedPackageSeries,
        productQuantities,
        serviceQuantities,
        packageQuantities,
        packageSeriesQuantities,
        productPrices,
        servicePrices,
        packagePrices,
        packageSeriesPrices,
        productDiscounts,
        serviceDiscounts,
        packageDiscounts,
        packageSeriesDiscounts,
        productTaxes,
        serviceTaxes,
        packageTaxes,
        packageSeriesTaxes,
      entityItems,
      showEntityModal: false,
      selectedModalItems: {},
      modalEntityType: '',
      modalSearchTerm: '',
      dropdownOpen: {},
      addButtonDropdownOpen: false,
      showMembershipModal: false,
      selectedMembershipInstallments: {},
      customerMemberships: [],
      installmentNames: {},
      entityNames: {},
    });
    
    // Load customer memberships if there are installment items
    const hasInstallments = entityItems.some(item => item.m_InstallmentId);
    if (hasInstallments && (item.invoice_CustomerId || item.Invoice_CustomerId)) {
      try {
        const customerId = item.invoice_CustomerId || item.Invoice_CustomerId;
        const response = await getCustomerMemberships({ customerId: customerId });
        setForm(prev => ({
          ...prev,
          customerMemberships: response || []
        }));
      } catch (error) {
        console.error('Error loading customer memberships for edit:', error);
      }
    }
    
    setIsEditing(true);
    setActiveTab("1");
  };

  const handleSalesReturn = async (invoice) => {
    try {
      setLoading(true);
      const invoiceData = await getSalesInvoiceForReturn(invoice.invoice_Id || invoice.Invoice_Id);
      
      // Handle different response structures - API might return the data directly or wrapped
      let invoiceDataToUse = invoiceData;
      if (invoiceData && typeof invoiceData === 'object') {
        // If response has a data property, use it
        if (invoiceData.data) {
          invoiceDataToUse = invoiceData.data;
        }
        // If response is an array, take first item
        if (Array.isArray(invoiceData) && invoiceData.length > 0) {
          invoiceDataToUse = invoiceData[0];
        }
      }
      
      if (!invoiceDataToUse || (!invoiceDataToUse.invoice_Id && !invoiceDataToUse.Invoice_Id)) {
        console.error("Invalid invoice data for return:", invoiceDataToUse);
        Swal.fire("Error", "Failed to load invoice for return: Invalid data received", "error");
        setLoading(false);
        return;
      }

      // Load details - only products for return
      // Check first if there are products with available stock BEFORE setting return mode
      const details = invoiceDataToUse.details || invoiceDataToUse.Details || [];
      const entityItems = [];
      
      if (details && Array.isArray(details) && details.length > 0) {
        details.forEach((detail, index) => {
          // Check if it's a product and has stock details
          const entityType = detail.entityType || detail.EntityType;
          const stockDetails = detail.stockDetails || detail.StockDetails || [];
          
          if (entityType === 'pro' && stockDetails && stockDetails.length > 0) {
            // Map stock details to include all necessary fields for stock selection modal
            const mappedStockDetails = stockDetails.map(stock => ({
              purchaseStock_Id: stock.stockTransaction_StockId || stock.purchaseStock_Id,
              purchaseStock_Product_Id: detail.entityId || detail.EntityId,
              purchaseStock_Unit_Id: stock.purchaseStock_Unit_Id || null,
              purchaseStock_BatchNo: stock.purchaseStock_BatchNo || stock.BatchNo,
              purchaseStock_ExpDate: stock.purchaseStock_ExpDate || stock.ExpDate,
              purchaseStock_IsAvailable: stock.purchaseStock_IsAvailable !== undefined ? stock.purchaseStock_IsAvailable : false,
              purchaseStock_SalesPrice: stock.purchaseStock_SalesPrice || stock.SalesPrice || detail.price || detail.Price,
              purchaseStock_CostPrice: stock.purchaseStock_CostPrice || stock.CostPrice || detail.cost || detail.Cost,
              purchaseStock_IsBonus: stock.purchaseStock_IsBonus !== undefined ? stock.purchaseStock_IsBonus : false,
              purchaseStock_Branch_Id: stock.purchaseStock_Branch_Id || invoiceDataToUse.invoice_BranchId || invoiceDataToUse.Invoice_BranchId
            }));
            
            // Only add products that have stock available for return
            const entityItem = {
              id: index + 1,
              entityId: detail.entityId || detail.EntityId,
              entityType: 'pro',
              type: 'product',
              entityName: detail.product_NameEn || detail.Product_NameEn || `Product ${detail.entityId || detail.EntityId}`,
              quantity: 0, // Reset to 0 for return
              unitPrice: detail.price || detail.Price || 0,
              price: detail.price || detail.Price || 0,
              discount: detail.disPer || detail.DisPer || 0,
              discountAmount: 0,
              tax: parseFloat((detail.taxPercent || detail.TaxPercent || "0")?.toString().split(',')[0]) || 0,
              taxAmount: 0,
              netAmount: 0,
              cost: detail.cost || detail.Cost || 0,
              salesmanId: detail.salesman_ID || detail.Salesman_ID || 0,
              stockDetails: mappedStockDetails, // Use mapped stock details
              stockData: null // Will be set when user selects stocks
            };
            entityItems.push(entityItem);
          }
        });
      }
      
      // Check if there are any products with available stock before proceeding
      if (entityItems.length === 0) {
        Swal.fire("Info", "No products with available stock found for return in this invoice.", "info");
        setLoading(false);
        return; // Exit early - don't set return mode or redirect
      }

      // Reset form and set return mode (only if we have products to return)
      setIsReturnMode(true);
      setReturnSalesInvoiceId(invoiceDataToUse.invoice_Id || invoiceDataToUse.Invoice_Id);
      setOriginalInvoiceNo(invoiceDataToUse.invoice_No || invoiceDataToUse.Invoice_No);
      // Store original invoice payment info for return mode restrictions
      setOriginalInvoicePaymentMethod(invoiceDataToUse.paymentMethod || invoiceDataToUse.PaymentMethod || "ca");
      setOriginalInvoiceType(invoiceDataToUse.invoice_Type || invoiceDataToUse.Invoice_Type || "ca");
      setIsEditing(false);
      setIsReadOnly(false);

      // Set form data (pre-populate with original invoice data)
      const customerId = invoiceDataToUse.invoice_CustomerId || invoiceDataToUse.Invoice_CustomerId;
      const customer = customers.find(c => c.customer_Id === customerId);
      if (customer) {
        setSelectedCustomerOption({
          value: customer.customer_Id,
          label: customer.customer_NameEn
        });
      }
      
      // Get original invoice payment details
      const originalPaymentMethod = invoiceDataToUse.paymentMethod || invoiceDataToUse.PaymentMethod || "ca";
      const originalInvoiceType = invoiceDataToUse.invoice_Type || invoiceDataToUse.Invoice_Type || "ca";
      
      setForm(prev => ({
        ...prev,
        Invoice_Id: 0,
        Invoice_No: "",
        Invoice_Date: new Date(),
        Invoice_CustomerId: customerId,
        Invoice_BusinessId: invoiceDataToUse.invoice_BusinessId || invoiceDataToUse.Invoice_BusinessId,
        Invoice_BranchId: invoiceDataToUse.invoice_BranchId || invoiceDataToUse.Invoice_BranchId,
        Invoice_Type: originalInvoiceType,
        PaymentMethod: originalPaymentMethod,
        // Don't pre-fill cash/cashless amounts - they will be populated after stock selection
        CashAmount: null,
        CashlessAmount: null,
        entityItems: entityItems,
        selectedStockData: {},
        preloadedStockItems: null
      }));

      // Generate return invoice number (format: OriginalInvoiceNoR001, R002, etc.)
      if (invoiceDataToUse.invoice_BusinessId && invoiceDataToUse.invoice_No) {
        const originalNo = invoiceDataToUse.invoice_No || invoiceDataToUse.Invoice_No;
        try {
          const result = await getNextSalesInvoiceNumber(
            invoiceDataToUse.invoice_BusinessId || invoiceDataToUse.Invoice_BusinessId,
            true, // isReturn = true
            originalNo.toString()
          );
          if (result && result.nextInvoiceNumber) {
            setForm(prev => ({
              ...prev,
              Invoice_No: String(result.nextInvoiceNumber) // Ensure it's a string
            }));
          } else {
            // Fallback: show placeholder
            setForm(prev => ({
              ...prev,
              Invoice_No: `${originalNo}R001` // Temporary placeholder
            }));
          }
        } catch (error) {
          console.error("Error generating return invoice number:", error);
          // Fallback: show placeholder
          setForm(prev => ({
            ...prev,
            Invoice_No: `${originalNo}R001` // Temporary placeholder
          }));
        }
      }

      setActiveTab("1");
      setLoading(false);
    } catch (error) {
      console.error("Error loading invoice for return:", error);
      Swal.fire("Error", `Failed to load invoice for return: ${error.response?.data?.message || error.message || "Unknown error"}`, "error");
      setLoading(false);
    }
  };

  const handleView = async (item) => {
    try {
      // Format date
      const invoiceDate = item.invoice_Date || item.Invoice_Date;
      
      // Format amounts
      const totalAmount = (item.invoice_TotalAmount || item.Invoice_TotalAmount || 0).toFixed(2);
      const discountAmount = (item.invoice_DisAmount || item.Invoice_DisAmount || 0).toFixed(2);
      const taxAmount = (item.invoice_TaxAmount || item.Invoice_TaxAmount || 0).toFixed(2);
      const netAmount = (item.invoice_NetAmount || item.Invoice_NetAmount || 0).toFixed(2);
      const paidAmount = (item.paidAmount || item.PaidAmount || 0).toFixed(2);
      const cashAmount = (item.cashAmount || item.CashAmount || 0).toFixed(2);
      const cashlessAmount = (item.cashlessAmount || item.CashlessAmount || 0).toFixed(2);
      
      // Get business name
      const businessName = getBusinessName(item.invoice_BusinessId || item.Invoice_BusinessId);
      
      // Get branch name
      const branchName = getBranchName(item.invoice_BranchId || item.Invoice_BranchId);
      
      // Helper function to get entity name
      const getEntityName = (entityType, entityId) => {
        if (entityType === 'pro') {
          const product = products.find(p => p.product_Id === entityId);
          return product ? product.product_NameEn : `Product ID: ${entityId}`;
        } else if (entityType === 'ser') {
          const service = services.find(s => s.spaService_Id === entityId);
          return service ? service.spaServiceName_En : `Service ID: ${entityId}`;
        } else if (entityType === 'pac') {
          const pkg = packages.find(p => p.packageId === entityId);
          return pkg ? pkg.packageName_En : `Package ID: ${entityId}`;
        } else if (entityType === 'sep') {
          const series = packageSeries.find(s => s.seriesId === entityId);
          return series ? series.seriesName_En : `Package Series ID: ${entityId}`;
        }
        return `Entity ID: ${entityId}`;
      };
      
      // Helper function to get tax name
      const getTaxName = (taxId) => {
        const tax = taxes.find(t => t.tax_Id === taxId);
        return tax ? tax.tax_NameEn : `Tax ID: ${taxId}`;
      };
      
      // Helper function to get invoice type name
      const getInvoiceTypeName = (type) => {
        if (!type) return "N/A";
        switch (type.toLowerCase()) {
          case "cr": return "Credit";
          case "ca": return "Cash";
          default: return type.toUpperCase();
        }
      };
      
      // Separate details by type
      let productsHtml = '';
      let servicesHtml = '';
      let packagesHtml = '';
      let packageSeriesHtml = '';
      
      if (item.details && item.details.length > 0) {
        const products = item.details.filter(detail => detail.entityType === 'pro');
        const services = item.details.filter(detail => detail.entityType === 'ser');
        const packages = item.details.filter(detail => detail.entityType === 'pac');
        const packageSeries = item.details.filter(detail => detail.entityType === 'sep');
        
        // Products section
        if (products.length > 0) {
          // Helper function to get salesman name
          const getSalesmanName = (salesmanId) => {
            if (!salesmanId || salesmanId === 0) return 'N/A';
            const employee = employees.find(emp => emp.employee_Id === salesmanId);
            return employee ? employee.employee_NameEn : `Employee ID: ${salesmanId}`;
          };
          
          productsHtml = `
            <div class="mt-3">
              <h6 class="text-success">Products</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Product Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                      <th>Salesman</th>
                      <th>Stock Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${products.map(detail => `
                      <tr>
                        <td>${getEntityName('pro', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                        <td>${getSalesmanName(detail.salesman_ID || detail.Salesman_ID)}</td>
                        <td>
                          <button 
                            class="btn btn-outline-info btn-sm" 
                            onclick="window.openStockDetailsModal('${getEntityName('pro', detail.entityId)}', ${JSON.stringify(detail.stockDetails || []).replace(/"/g, '&quot;')})"
                            title="View Stock Details"
                            style="padding: 4px 8px;"
                          >
                            <i class="bx bx-box" style="font-size: 14px;"></i>
                          </button>
                        </td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }
        
        // Services section
        if (services.length > 0) {
          servicesHtml = `
            <div class="mt-3">
              <h6 class="text-info">Services</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Service Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${services.map(detail => `
                      <tr>
                        <td>${getEntityName('ser', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }
        
        // Packages section
        if (packages.length > 0) {
          packagesHtml = `
            <div class="mt-3">
              <h6 class="text-warning">Packages</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Package Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${packages.map(detail => `
                      <tr>
                        <td>${getEntityName('pac', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }
        
        // Package Series section
        if (packageSeries.length > 0) {
          packageSeriesHtml = `
            <div class="mt-3">
              <h6 class="text-secondary">Package Series</h6>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-light">
                    <tr>
                      <th>Package Series Name</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${packageSeries.map(detail => `
                      <tr>
                        <td>${getEntityName('sep', detail.entityId)}</td>
                        <td>${detail.quantity}</td>
                        <td>${(detail.price || 0).toFixed(2)}</td>
                        <td>${(detail.disAmount || 0).toFixed(2)}</td>
                        <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
          `;
        }
        
        // Check for installment details and fetch installment information
        const installmentDetails = item.details.filter(detail => detail.m_InstallmentId);
        let installmentHtml = '';
        
        if (installmentDetails.length > 0) {
          try {
            // Get unique installment IDs
            const installmentIds = [...new Set(installmentDetails.map(detail => detail.m_InstallmentId))];
            
            // Fetch installment details
            const installmentData = await Promise.all(
              installmentIds.map(async (installmentId) => {
                try {
                  const response = await getCustomerMemberships({ 
                    installmentId: installmentId 
                  });
                  const membership = response.find(m => 
                    m.installments?.some(inst => inst.m_InstallmentId === installmentId)
                  );
                  
                  // Also fetch package series data to get installment names
                  let packageSeriesData = null;
                  if (membership && membership.entity_Type === 'SER') {
                    try {
                      const seriesResponse = await getSpaPackageSeries({ seriesId: membership.entity_Id });
                      packageSeriesData = seriesResponse.find(s => s.seriesId === membership.entity_Id);
                    } catch (error) {
                      console.error(`Error fetching package series ${membership.entity_Id}:`, error);
                    }
                  }
                  
                  return { membership, packageSeriesData };
                } catch (error) {
                  console.error(`Error fetching installment ${installmentId}:`, error);
                  return null;
                }
              })
            );
            
            // Group installment details by entity type
            const installmentPackages = installmentDetails.filter(detail => detail.entityType === 'pac');
            const installmentPackageSeries = installmentDetails.filter(detail => detail.entityType === 'sep');
            
            // Add installment info to packages section
            if (installmentPackages.length > 0) {
              const installmentPackageRows = installmentPackages.map(detail => {
                const data = installmentData.find(d => 
                  d?.membership?.installments?.some(inst => inst.m_InstallmentId === detail.m_InstallmentId)
                );
                const installment = data?.membership?.installments?.find(inst => inst.m_InstallmentId === detail.m_InstallmentId);
                const installmentName = installment ? `Installment ${installment.installmentId}` : 'Unknown Installment';
                
                return `
                  <tr>
                    <td>${getEntityName('pac', detail.entityId)} - ${installmentName}</td>
                    <td>${detail.quantity}</td>
                    <td>${(detail.price || 0).toFixed(2)}</td>
                    <td>${(detail.disAmount || 0).toFixed(2)}</td>
                    <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                  </tr>
                `;
              }).join('');
              
              packagesHtml += `
                <div class="mt-3">
                  <h6 class="text-warning">Installment Packages</h6>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                      <thead class="table-light">
                        <tr>
                          <th>Package Name</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Discount</th>
                          <th>Tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${installmentPackageRows}
                      </tbody>
                    </table>
                  </div>
                </div>
              `;
            }
            
            // Add installment info to package series section
            if (installmentPackageSeries.length > 0) {
              const installmentPackageSeriesRows = installmentPackageSeries.map(detail => {
                const data = installmentData.find(d => 
                  d?.membership?.installments?.some(inst => inst.m_InstallmentId === detail.m_InstallmentId)
                );
                const installment = data?.membership?.installments?.find(inst => inst.m_InstallmentId === detail.m_InstallmentId);
                
                // Get proper installment name from package series data
                let installmentName = 'Unknown Installment';
                if (installment && data?.packageSeriesData?.installments) {
                  const seriesInstallment = data.packageSeriesData.installments.find(
                    si => si.installmentId === installment.installmentId
                  );
                  installmentName = seriesInstallment ? seriesInstallment.installmentName : `Installment ${installment.installmentId}`;
                } else if (installment) {
                  installmentName = `Installment ${installment.installmentId}`;
                }
                
                return `
                  <tr>
                    <td>${getEntityName('sep', detail.entityId)} - ${installmentName}</td>
                    <td>${detail.quantity}</td>
                    <td>${(detail.price || 0).toFixed(2)}</td>
                    <td>${(detail.disAmount || 0).toFixed(2)}</td>
                    <td>${(detail.taxAmt || 0).toFixed(2)}</td>
                  </tr>
                `;
              }).join('');
              
              packageSeriesHtml += `
                <div class="mt-3">
                  <h6 class="text-secondary">Installment Package Series</h6>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                      <thead class="table-light">
                        <tr>
                          <th>Package Series Name</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Discount</th>
                          <th>Tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${installmentPackageSeriesRows}
                      </tbody>
                    </table>
                  </div>
                </div>
              `;
            }
          } catch (error) {
            console.error('Error fetching installment details:', error);
          }
        }
      }
      
      // Build taxes HTML (remove duplicates)
      let taxesHtml = '';
      if (item.taxes && item.taxes.length > 0) {
        // Create unique tax entries by tax_Id and taxPer
        const uniqueTaxes = item.taxes.reduce((acc, tax) => {
          const key = `${tax.tax_Id}_${tax.taxPer}`;
          if (!acc.some(t => `${t.tax_Id}_${t.taxPer}` === key)) {
            acc.push(tax);
          }
          return acc;
        }, []);
        
        taxesHtml = `
          <div class="mt-3">
            <h6 class="text-primary">Tax Details</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Tax Name</th>
                    <th>Tax Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  ${uniqueTaxes.map(tax => `
                    <tr>
                      <td>${getTaxName(tax.tax_Id)}</td>
                      <td>${(tax.taxPer || 0).toFixed(2)}%</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        `;
      }
      
      // Add global function for stock details modal
      window.openStockDetailsModal = (productName, stockDetails) => {
        handleOpenStockDetailsModal(productName, stockDetails);
      };
      
      Swal.fire({
        title: `Invoice #${item.invoice_No || item.Invoice_No || 'N/A'}`,
        html: `
          <div class="text-start" style="max-height: 70vh; overflow-y: auto;">
            <div class="row">
              <div class="col-md-6">
                <h6 class="text-primary">Basic Information</h6>
                <p><strong>Invoice Number:</strong> ${item.invoice_No || item.Invoice_No || 'N/A'}</p>
                <p><strong>Invoice Date:</strong> ${invoiceDate ? new Date(invoiceDate).toLocaleDateString() : 'N/A'}</p>
                <p><strong>Customer:</strong> ${getCustomerName(item.invoice_CustomerId || item.Invoice_CustomerId)}</p>
                <p><strong>Business:</strong> ${businessName}</p>
                <p><strong>Branch:</strong> ${branchName}</p>
                <p><strong>Invoice Type:</strong> ${getInvoiceTypeName(item.invoice_Type || item.Invoice_Type)}</p>
              </div>
              <div class="col-md-6">
                <h6 class="text-primary">Financial Summary</h6>
                <p><strong>Total Quantity:</strong> ${item.invoice_TotalQty || item.Invoice_TotalQty || 0}</p>
                <p><strong>Total Amount:</strong> ${totalAmount}</p>
                <p><strong>Discount Amount:</strong> ${discountAmount}</p>
                <p><strong>Tax Amount:</strong> ${taxAmount}</p>
                <p><strong>Net Amount:</strong> <span class="fw-bold text-success">${netAmount}</span></p>
                <p><strong>Payment Method:</strong> ${getPaymentMethodName(item.paymentMethod || item.PaymentMethod)}</p>
              </div>
            </div>
            
            <div class="row mt-3">
              <div class="col-md-6">
                <h6 class="text-primary">Payment Details</h6>
                <p><strong>Paid Amount:</strong> ${paidAmount}</p>
                <p><strong>Cash Amount:</strong> ${cashAmount}</p>
                <p><strong>Cashless Amount:</strong> ${cashlessAmount}</p>
                <p><strong>Auth Code:</strong> ${item.authCode || item.AuthCode || 'N/A'}</p>
              </div>
            </div>
            
            ${productsHtml}
            ${servicesHtml}
            ${packagesHtml}
            ${packageSeriesHtml}
            ${taxesHtml}
          </div>
        `,
        icon: "info",
        width: '90%',
        confirmButtonText: 'Close',
        showCloseButton: true,
        customClass: {
          popup: 'swal-wide'
        }
      });
    } catch (error) {
      console.error("Error viewing invoice:", error);
    }
  };

  const handlePrintInvoice = async (item) => {
    try {
      setPrintLoading(true);
      const invoiceId = item.invoice_Id || item.Invoice_Id;
      
      // Fetch invoice data for print
      const invoiceData = await getInvoiceForPrint(invoiceId);
      
      if (!invoiceData || !invoiceData.header) {
        Swal.fire("Error", "Invoice not found or unable to load invoice data", "error");
        setPrintLoading(false);
        return;
      }
      
      setInvoicePrintData(invoiceData);
      setShowInvoicePrintModal(true);
      setPrintLoading(false);
    } catch (error) {
      console.error("Error loading invoice for print:", error);
      Swal.fire("Error", `Failed to load invoice: ${error.response?.data?.message || error.message || "Unknown error"}`, "error");
      setPrintLoading(false);
    }
  };

  // Cleanup stock locks on page refresh/navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Note: Modern browsers may ignore async operations in beforeunload
      // So we'll also cleanup on component unmount
      if (temporaryFormUniqueId) {
        // Try to cleanup synchronously if possible
        navigator.sendBeacon && navigator.sendBeacon(
          `${window.location.origin}/api/StockSelectionLock/delete-lock`,
          JSON.stringify({ temporaryFormUniqueId })
        );
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Cleanup on component unmount (page navigation, route change)
      if (temporaryFormUniqueId) {
        deleteStockSelectionLock(temporaryFormUniqueId).catch(() => {});
      }
    };
  }, [temporaryFormUniqueId]);

  const resetForm = () => {
    // Cleanup stock locks before reset
    if (temporaryFormUniqueId) {
      deleteStockSelectionLock(temporaryFormUniqueId).catch(err => 
        console.error('Error cleaning up locks on reset:', err)
      );
    }
    
    setForm(defaultForm);
    setIsEditing(false);
    setSelectedCustomerOption(null);
    setCustomerHasMembership(false);
    // Reset return mode
    setIsReturnMode(false);
    setReturnSalesInvoiceId(null);
    setOriginalInvoiceNo(null);
    setOriginalInvoicePaymentMethod(null);
    setOriginalInvoiceType(null);
    // Reset form ID
    setTemporaryFormUniqueId(null);
    // Generate new invoice number after clearing form
    loadNextInvoiceNumber();
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setCustomerSearchOptions([]);
    setSelectedCustomerFilterOption(null);
    setHasAppliedFilter(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Helper functions for quantity management
  const handleQuantityChange = (entityType, entityId, quantity) => {
    const quantityField = `${entityType}Quantities`;
    // Allow empty string or valid numbers, set minimum to 1 only when value is provided
    const parsedQuantity = quantity === '' ? '' : (parseInt(quantity) || 1);
    setForm(prev => ({
      ...prev,
      [quantityField]: {
        ...prev[quantityField],
        [entityId]: parsedQuantity
      }
    }));
  };

  const getQuantity = (entityType, entityId) => {
    const quantityField = `${entityType}Quantities`;
    const quantity = form[quantityField][entityId];
    return quantity === '' ? '' : (quantity || 1);
  };

  // Helper functions for price, discount, tax management
  const handlePriceChange = (entityType, entityId, price) => {
    const priceField = `${entityType}Prices`;
    setForm(prev => ({
      ...prev,
      [priceField]: {
        ...prev[priceField],
        [entityId]: parseFloat(price) || 0
      }
    }));
  };

  const handleDiscountChange = (entityType, entityId, discount) => {
    const discountField = `${entityType}Discounts`;
    setForm(prev => ({
      ...prev,
      [discountField]: {
        ...prev[discountField],
        [entityId]: parseFloat(discount) || 0
      }
    }));
  };

  const handleTaxChange = (entityType, entityId, tax) => {
    const taxField = `${entityType}Taxes`;
    setForm(prev => ({
      ...prev,
      [taxField]: {
        ...prev[taxField],
        [entityId]: parseFloat(tax) || 0
      }
    }));
  };

  const getPrice = (entityType, entityId) => {
    const priceField = `${entityType}Prices`;
    const unitPrice = form[priceField][entityId] || 0;
    const quantity = getQuantity(entityType, entityId) || 1;
    return unitPrice * quantity;
  };

  const getDiscount = (entityType, entityId) => {
    const discountField = `${entityType}Discounts`;
    return form[discountField][entityId] || 0;
  };

  const getTax = (entityType, entityId) => {
    const taxField = `${entityType}Taxes`;
    return form[taxField][entityId] || 0;
  };

  // Calculate entity totals from entityItems array
  const getEntityTotalsFromItems = (entityType) => {
    let totalQty = 0;
    let totalAmount = 0;
    let totalDiscountAmount = 0;
    let totalTaxAmount = 0;

    // For packageSeries, separate regular items from installment items
    let items;
    if (entityType === 'packageSeries') {
      items = form.entityItems.filter(item => item.type === entityType && !item.m_InstallmentId);
    } else {
      items = form.entityItems.filter(item => item.type === entityType);
    }
    
    items.forEach(item => {
      const lineTotal = (item.unitPrice ?? 0) * item.quantity;
      const discountAmount = item.discountAmount || 0;
      const taxAmount = item.taxAmount || 0;
      
      totalQty += item.quantity;
      totalAmount += lineTotal;
      totalDiscountAmount += discountAmount;
      totalTaxAmount += taxAmount;
    });

    // Calculate effective rates based on totals
    const effectiveDiscountPercentage = totalAmount > 0 
      ? (totalDiscountAmount / totalAmount) * 100 
      : 0;

    const taxableValue = totalAmount - totalDiscountAmount;
    const effectiveTaxPercentage = taxableValue > 0
      ? (totalTaxAmount / taxableValue) * 100
      : 0;

    // Calculate net amount: totalAmount - discount + tax
    const netAmount = totalAmount - totalDiscountAmount + totalTaxAmount;

    return { 
      totalQty, 
      totalAmount, 
      totalDiscountAmount, 
      totalTaxAmount, 
      effectiveDiscountPercentage, 
      effectiveTaxPercentage,
      netAmount
    };
  };

  // Calculate installment totals from entityItems array
  const getInstallmentTotalsFromItems = () => {
    let totalQty = 0;
    let totalAmount = 0;
    let totalDiscountAmount = 0;
    let totalTaxAmount = 0;

    // Get all installment items (both package and packageSeries with m_InstallmentId)
    const installmentItems = form.entityItems.filter(item => item.m_InstallmentId);
    
    installmentItems.forEach(item => {
      const lineTotal = (item.unitPrice ?? 0) * item.quantity;
      const discountAmount = item.discountAmount || 0;
      const taxAmount = item.taxAmount || 0;
      
      totalQty += item.quantity;
      totalAmount += lineTotal;
      totalDiscountAmount += discountAmount;
      totalTaxAmount += taxAmount;
    });

    // Calculate effective rates based on totals
    const effectiveDiscountPercentage = totalAmount > 0 
      ? (totalDiscountAmount / totalAmount) * 100 
      : 0;

    const taxableValue = totalAmount - totalDiscountAmount;
    const effectiveTaxPercentage = taxableValue > 0
      ? (totalTaxAmount / taxableValue) * 100
      : 0;

    // Calculate net amount: totalAmount - discount + tax
    const netAmount = totalAmount - totalDiscountAmount + totalTaxAmount;

    return { 
      totalQty, 
      totalAmount, 
      totalDiscountAmount, 
      totalTaxAmount, 
      effectiveDiscountPercentage, 
      effectiveTaxPercentage,
      netAmount
    };
  };

  // Calculate entity totals
  const getEntityTotals = (entityType) => {
    let totalQty = 0;
    let totalAmount = 0;
    let totalDiscountAmount = 0;
    let totalTaxAmount = 0;

            const selectedItems = form[`selected${(entityType || '').charAt(0).toUpperCase() + (entityType || '').slice(1)}s`] || [];
    
    selectedItems.forEach(item => {
      const uniqueKey = item.uniqueKey || item.value; // Fallback for backward compatibility
      const qty = getQuantity(entityType, uniqueKey) || 1;
      const unitPrice = form[`${entityType}Prices`][uniqueKey] || 0;
      const discountPercentage = getDiscount(entityType, uniqueKey);
      const taxPercentage = getTax(entityType, uniqueKey);
      
      const lineTotal = unitPrice * qty;
      // Cap discount at 100% per line item
      const cappedDiscountPercentage = Math.min(discountPercentage, 100);
      const discountAmount = (lineTotal * cappedDiscountPercentage) / 100;
      const taxableValue = Math.max(lineTotal - discountAmount, 0); // Ensure taxable value is not negative
      const taxAmount = (taxableValue * taxPercentage) / 100;
      
      totalQty += qty;
      totalAmount += lineTotal;
      totalDiscountAmount += discountAmount;
      totalTaxAmount += taxAmount;
    });

    // Calculate effective rates based on totals
    const effectiveDiscountPercentage = totalAmount > 0 
      ? (totalDiscountAmount / totalAmount) * 100 
      : 0;

    const taxableValue = totalAmount - totalDiscountAmount;
    const effectiveTaxPercentage = taxableValue > 0
      ? (totalTaxAmount / taxableValue) * 100
      : 0;

    return { 
      totalQty, 
      totalAmount, 
      totalDiscountAmount, 
      totalTaxAmount, 
      effectiveDiscountPercentage, 
      effectiveTaxPercentage 
    };
  };

  // Helper functions for entity item management
  const addEntityItem = async (entityType, entityData, quantity = 1, stockData = null) => {
    // Generate form ID when first product is added (for stock selection lock)
    if (entityType === 'product' && !temporaryFormUniqueId) {
      const formId = generateTemporaryFormUniqueId();
      setTemporaryFormUniqueId(formId);
    }
    
    const uniqueId = Date.now() + Math.random(); // More unique ID generation
    const newItem = {
      id: uniqueId,
      type: entityType,
      entityId: entityData.value,
      entityName: entityData.label,
      quantity: quantity,
      unitPrice: null,
      totalPrice: 0,
      discount: null,
      tax: 0,
      taxAmount: 0,
      netAmount: 0,
      discountAmount: 0, // Store discount amount
      costPrice: 0, // Store cost price separately
      taxIds: [], // Store all Tax IDs for API
      taxBreakdown: [], // Store tax breakdown for display
      uniqueKey: uniqueId, // Add unique key for legacy arrays
      salesmanId: 0, // Add salesman ID (0 for non-products, will be set for products)
      stockData: stockData // Store stock data for API
    };

    // Fetch cost from API based on entity type
    const cost = await fetchEntityCost(entityType, entityData.value);
    newItem.costPrice = cost;

    // Populate price and other details based on entity type
    let entityDetails = null;
    if (entityType === 'product') {
      entityDetails = products.find(p => p.product_Id === entityData.value);
      if (entityDetails) {
        // Use stock data if available, otherwise use product data
        if (stockData) {
          newItem.unitPrice = stockData.price || null;
          newItem.discount = stockData.discount || null; // Use stock discount percentage
          // Convert stock data to comma-separated stock IDs for API
          const stockIds = stockData.stocks ? stockData.stocks.map(s => s.purchaseStock_Id).join(',') : '';
          newItem.stockData = stockIds; // Store stock IDs as comma-separated string
          newItem.entityName = `${entityData.label} (${stockData.batchNumbers})`; // Include batch info
        } else {
          newItem.unitPrice = entityDetails.product_CostPrice || null; // Start with cost price as unit price
          newItem.discount = entityDetails.product_DiscountPercentage || null;
        }
        newItem.taxIds = entityDetails.taxIds || [];
        newItem.taxBreakdown = calculateTaxBreakdown(entityDetails.taxIds);
        newItem.tax = calculateCombinedTaxPercentage(entityDetails.taxIds);
      }
    } else if (entityType === 'service') {
      entityDetails = services.find(s => s.spaService_Id === entityData.value);
      if (entityDetails) {
        newItem.unitPrice = entityDetails.amount || null;
        newItem.discount = entityDetails.discountPercentage || null;
        newItem.taxIds = entityDetails.taxIds || [];
        newItem.taxBreakdown = calculateTaxBreakdown(entityDetails.taxIds);
        newItem.tax = calculateCombinedTaxPercentage(entityDetails.taxIds);
      }
    } else if (entityType === 'package') {
      entityDetails = packages.find(p => p.packageId === entityData.value);
      if (entityDetails) {
        newItem.unitPrice = entityDetails.amount || null;
        newItem.discount = entityDetails.discountPercentage || null;
        newItem.taxIds = entityDetails.taxIds || [];
        newItem.taxBreakdown = calculateTaxBreakdown(entityDetails.taxIds);
        newItem.tax = calculateCombinedTaxPercentage(entityDetails.taxIds);
      }
    } else if (entityType === 'packageSeries') {
      entityDetails = packageSeries.find(s => s.seriesId === entityData.value);
      if (entityDetails) {
        newItem.unitPrice = entityDetails.amount || null;
        newItem.discount = entityDetails.discountPercentage || null;
        newItem.taxIds = entityDetails.taxIds || [];
        newItem.taxBreakdown = calculateTaxBreakdown(entityDetails.taxIds);
        newItem.tax = calculateCombinedTaxPercentage(entityDetails.taxIds);
      }
    }

    // Calculate derived amounts (handle null values)
    const unitPrice = newItem.unitPrice ?? 0;
    const discount = newItem.discount ?? 0;
    newItem.totalPrice = unitPrice * quantity;
    newItem.discountAmount = (unitPrice * discount / 100) * quantity;
    newItem.taxAmount = ((unitPrice * quantity - newItem.discountAmount) * newItem.tax / 100);
    newItem.netAmount = (unitPrice * quantity) - newItem.discountAmount + newItem.taxAmount;

    setForm(prev => ({
      ...prev,
      entityItems: [...prev.entityItems, newItem]
    }));

    // Also update the old arrays for backward compatibility with totals calculation
    updateLegacyArrays(entityType, entityData, newItem);
  };

  // Helper function to add entity item with custom unit price (for membership installments)
  const addEntityItemWithCustomPrice = async (entityType, entityData, quantity = 1, customUnitPrice = 0, parentEntityData = null) => {
    const uniqueId = Date.now() + Math.random(); // More unique ID generation
    
    // Fetch cost from API based on entity type
    const cost = await fetchEntityCost(entityType, entityData.value);
    
    // Get discount and tax from parent entity (package series) if available
    const discountPercentage = parentEntityData?.discountPercentage || 0;
    
    // Calculate total tax percentage from tax IDs
    let taxPercentage = 0;
    if (parentEntityData?.taxIds && parentEntityData.taxIds.length > 0) {
      // Sum up all tax percentages from tax IDs
      // Note: This assumes tax data is available in the component scope
      // You may need to fetch tax data or pass it as parameter
      taxPercentage = parentEntityData.taxIds.reduce((total, taxId) => {
        // Find tax data by ID and add percentage
        const taxData = taxes.find(t => t.tax_Id === taxId);
        return total + (taxData?.tax_Perc || 0);
      }, 0);
    }
    
    const totalPrice = customUnitPrice * quantity;
    const discountAmount = (totalPrice * discountPercentage) / 100;
    const taxableValue = Math.max(totalPrice - discountAmount, 0);
    const taxAmount = (taxableValue * taxPercentage) / 100;
    const netAmount = totalPrice - discountAmount + taxAmount;
    
    const newItem = {
      id: uniqueId,
      type: entityType,
      entityId: entityData.value,
      entityName: entityData.label,
      quantity: quantity,
      unitPrice: customUnitPrice,
      totalPrice: totalPrice,
      discount: discountPercentage,
      tax: taxPercentage,
      taxAmount: taxAmount,
      netAmount: netAmount,
      discountAmount: discountAmount,
      costPrice: cost, // Use fetched cost
      taxIds: parentEntityData?.taxIds || [], // Use parent entity tax IDs if available
      taxBreakdown: [], // Empty array as requested
      uniqueKey: uniqueId, // Add unique key for legacy arrays
      m_InstallmentId: entityData.m_InstallmentId, // Store the membership installment ID
      salesmanId: 0 // Add salesman ID (0 for non-products, will be set for products)
    };

    setForm(prev => ({
      ...prev,
      entityItems: [...prev.entityItems, newItem]
    }));

    // Also update the old arrays for backward compatibility with totals calculation
    updateLegacyArrays(entityType, entityData, newItem);
  };

  // Helper function to calculate combined tax percentage from multiple tax IDs
  const calculateCombinedTaxPercentage = (taxIds) => {
    if (!taxIds || taxIds.length === 0) return 0;
    
    let combinedPercentage = 0;
    taxIds.forEach(taxId => {
      const tax = taxes.find(t => t.tax_Id === taxId);
      if (tax && tax.tax_Perc) {
        combinedPercentage += tax.tax_Perc;
      }
    });
    
    return combinedPercentage;
  };

  // Helper function to calculate tax breakdown for display
  const calculateTaxBreakdown = (taxIds) => {
    if (!taxIds || taxIds.length === 0) return [];
    
    const breakdown = [];
    taxIds.forEach(taxId => {
      const tax = taxes.find(t => t.tax_Id === taxId);
      if (tax) {
        breakdown.push({
          id: tax.tax_Id,
          name: tax.tax_NameEn,
          percentage: tax.tax_Perc || 0
        });
      }
    });
    
    return breakdown;
  };

  const updateLegacyArrays = (entityType, entityData, itemData) => {
            const entityField = `selected${(entityType || '').charAt(0).toUpperCase() + (entityType || '').slice(1)}s`;
    const quantityField = `${entityType}Quantities`;
    const priceField = `${entityType}Prices`;
    const discountField = `${entityType}Discounts`;
    const taxField = `${entityType}Taxes`;

    // Create a unique entity object with the unique key and include taxIds
    const uniqueEntityData = {
      ...entityData,
      uniqueKey: itemData.uniqueKey,
      taxIds: itemData.taxIds || [] // Include taxIds for API compatibility
    };

    setForm(prev => ({
      ...prev,
      [entityField]: [...(prev[entityField] || []), uniqueEntityData],
      [quantityField]: {
        ...prev[quantityField],
        [itemData.uniqueKey]: itemData.quantity
      },
      [priceField]: {
        ...prev[priceField],
        [itemData.uniqueKey]: itemData.unitPrice
      },
      [discountField]: {
        ...prev[discountField],
        [itemData.uniqueKey]: itemData.discount
      },
      [taxField]: {
        ...prev[taxField],
        [itemData.uniqueKey]: itemData.tax
      }
    }));
  };

  const updateEntityItemQuantity = (itemId, quantity) => {
    setForm(prev => {
      const updatedEntityItems = prev.entityItems.map(item => {
        if (item.id === itemId) {
          const newQuantity = parseInt(quantity) || 1;
          const totalPrice = newQuantity * item.unitPrice;
          const discountAmount = (item.unitPrice * item.discount / 100) * newQuantity;
          const taxAmount = ((totalPrice - discountAmount) * item.tax / 100);
          const netAmount = totalPrice - discountAmount + taxAmount;
          
          return {
            ...item,
            quantity: newQuantity,
            totalPrice: totalPrice,
            discountAmount: discountAmount,
            taxAmount: taxAmount,
            netAmount: netAmount
          };
        }
        return item;
      });

      // Find the updated item to update legacy arrays
      const updatedItem = updatedEntityItems.find(item => item.id === itemId);
      if (updatedItem) {
        const quantityField = `${updatedItem.type}Quantities`;
        return {
          ...prev,
          entityItems: updatedEntityItems,
          [quantityField]: {
            ...prev[quantityField],
            [updatedItem.uniqueKey]: updatedItem.quantity
          }
        };
      }

      return {
        ...prev,
        entityItems: updatedEntityItems
      };
    });
  };

  const updateEntityItemUnitPrice = (itemId, unitPrice) => {
    setForm(prev => {
      const updatedEntityItems = prev.entityItems.map(item => {
        if (item.id === itemId) {
          const newUnitPrice = unitPrice === '' ? null : (parseFloat(unitPrice) || null);
          const unitPriceForCalc = newUnitPrice ?? 0;
          const discountForCalc = (item.discount ?? 0);
          const totalPrice = item.quantity * unitPriceForCalc;
          const discountAmount = (unitPriceForCalc * discountForCalc / 100) * item.quantity;
          const taxAmount = ((totalPrice - discountAmount) * item.tax / 100);
          const netAmount = totalPrice - discountAmount + taxAmount;
          
          // Validation for installment items
          let validationError = '';
          if (item.m_InstallmentId && unitPrice !== '' && unitPrice !== null && unitPrice !== undefined && !isNaN(unitPrice)) {
            // Find the original installment data
            const installment = prev.customerMemberships
              ?.flatMap(membership => membership.installments)
              ?.find(inst => inst.m_InstallmentId === item.m_InstallmentId);
            
            if (installment) {
              const remainingAmount = Math.round((installment.amount - installment.paidAmount) * 100) / 100;
              if (newUnitPrice > remainingAmount) {
                validationError = `Amount cannot exceed remaining balance of ${remainingAmount.toFixed(2)}`;
              }
            }
          } else if (item.m_InstallmentId) {
            // Clear validation error if unitPrice is empty or invalid
            validationError = '';
          }
          
          return {
            ...item,
            unitPrice: newUnitPrice,
            totalPrice: totalPrice,
            discountAmount: discountAmount,
            taxAmount: taxAmount,
            netAmount: netAmount,
            validationError: validationError
          };
        }
        return item;
      });

      // Find the updated item to update legacy arrays
      const updatedItem = updatedEntityItems.find(item => item.id === itemId);
      if (updatedItem) {
        const priceField = `${updatedItem.type}Prices`;
        return {
          ...prev,
          entityItems: updatedEntityItems,
          [priceField]: {
            ...prev[priceField],
            [updatedItem.uniqueKey]: updatedItem.unitPrice
          }
        };
      }

      return {
        ...prev,
        entityItems: updatedEntityItems
      };
    });
  };

  const updateEntityItemDiscount = (itemId, discount) => {
    setForm(prev => {
      const updatedEntityItems = prev.entityItems.map(item => {
        if (item.id === itemId) {
          const newDiscount = discount === '' ? null : (parseFloat(discount) || null);
          const unitPriceForCalc = (item.unitPrice ?? 0);
          const discountForCalc = (newDiscount ?? 0);
          const totalPrice = item.quantity * unitPriceForCalc;
          const discountAmount = (unitPriceForCalc * discountForCalc / 100) * item.quantity;
          const taxAmount = ((totalPrice - discountAmount) * item.tax / 100);
          const netAmount = totalPrice - discountAmount + taxAmount;
          
          return {
            ...item,
            discount: newDiscount,
            discountAmount: discountAmount,
            taxAmount: taxAmount,
            netAmount: netAmount
          };
        }
        return item;
      });

      // Find the updated item to update legacy arrays
      const updatedItem = updatedEntityItems.find(item => item.id === itemId);
      if (updatedItem) {
        const discountField = `${updatedItem.type}Discounts`;
        return {
          ...prev,
          entityItems: updatedEntityItems,
          [discountField]: {
            ...prev[discountField],
            [updatedItem.uniqueKey]: updatedItem.discount
          }
        };
      }

      return {
        ...prev,
        entityItems: updatedEntityItems
      };
    });
  };

  const updateEntityItemSalesman = (itemId, salesmanId) => {
    setForm(prev => {
      const updatedEntityItems = prev.entityItems.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            salesmanId: salesmanId
          };
        }
        return item;
      });

      return {
        ...prev,
        entityItems: updatedEntityItems
      };
    });
  };

  const removeEntityItem = (itemId) => {
    const itemToRemove = form.entityItems.find(item => item.id === itemId);
    if (!itemToRemove) return;

    setForm(prev => ({
      ...prev,
      entityItems: prev.entityItems.filter(item => item.id !== itemId)
    }));

    // Remove from legacy arrays
            const entityField = `selected${(itemToRemove.type || '').charAt(0).toUpperCase() + (itemToRemove.type || '').slice(1)}s`;
    const quantityField = `${itemToRemove.type}Quantities`;
    const priceField = `${itemToRemove.type}Prices`;
    const discountField = `${itemToRemove.type}Discounts`;
    const taxField = `${itemToRemove.type}Taxes`;

    setForm(prev => {
      const newLegacyArray = (prev[entityField] || []).filter(item => item.uniqueKey !== itemToRemove.uniqueKey);
      const newQuantities = { ...prev[quantityField] };
      const newPrices = { ...prev[priceField] };
      const newDiscounts = { ...prev[discountField] };
      const newTaxes = { ...prev[taxField] };
      
      delete newQuantities[itemToRemove.uniqueKey];
      delete newPrices[itemToRemove.uniqueKey];
      delete newDiscounts[itemToRemove.uniqueKey];
      delete newTaxes[itemToRemove.uniqueKey];

      return {
        ...prev,
        [entityField]: newLegacyArray,
        [quantityField]: newQuantities,
        [priceField]: newPrices,
        [discountField]: newDiscounts,
        [taxField]: newTaxes
      };
    });
  };

  // Modal helper functions
  const openEntityModal = (entityType) => {
    if (!form.Invoice_BusinessId || !form.Invoice_BranchId) {
      Swal.fire('Select Company and Branch', 'Please select a company and branch before adding items.', 'warning');
      return;
    }
    setForm(prev => ({
      ...prev,
      modalEntityType: entityType,
      showEntityModal: true,
      selectedModalItems: {},
      modalSearchTerm: ''
    }));
  };

  const closeEntityModal = () => {
    setForm(prev => ({
      ...prev,
      showEntityModal: false,
      selectedModalItems: {},
      selectedStockData: {}, // Clear stock data when closing modal
      modalEntityType: '',
      modalSearchTerm: ''
    }));
  };

  const handleModalItemCheck = (entityId, checked) => {
    setForm(prev => {
      const newSelectedModalItems = { ...prev.selectedModalItems };
      const newSelectedStockData = { ...prev.selectedStockData };
      
      if (checked) {
        // When checked, set quantity to 1
        newSelectedModalItems[entityId] = 1;
      } else {
        // When unchecked, remove from selectedModalItems and clear stock data
        delete newSelectedModalItems[entityId];
        delete newSelectedStockData[entityId];
      }
      
      return {
        ...prev,
        selectedModalItems: newSelectedModalItems,
        selectedStockData: newSelectedStockData
      };
    });
  };

  const handleModalQuantityChange = (entityId, quantity) => {
    setForm(prev => ({
      ...prev,
      selectedModalItems: {
        ...prev.selectedModalItems,
        [entityId]: quantity === '' ? '' : (parseInt(quantity) || 1)
      }
    }));
  };

  // Stock modal handlers
  const handleOpenStockModal = (productId, productName, quantity, itemIndex = null) => {
    // Generate form ID when opening stock modal if not already generated
    if (!temporaryFormUniqueId) {
      const formId = generateTemporaryFormUniqueId();
      setTemporaryFormUniqueId(formId);
    }
    
    // For return mode, get stock details from the entity item
    let preloadedStock = null;
    if (isReturnMode && itemIndex !== null) {
      const item = form.entityItems[itemIndex];
      if (item && item.stockDetails && item.stockDetails.length > 0) {
        // Map stock details to format expected by StockSelectionModal
        preloadedStock = item.stockDetails.map(stock => ({
          purchaseStock_Id: stock.purchaseStock_Id || stock.stockTransaction_StockId,
          purchaseStock_Product_Id: stock.purchaseStock_Product_Id || stock.purchaseStock_Product_Id,
          purchaseStock_Unit_Id: stock.purchaseStock_Unit_Id || stock.purchaseStock_Unit_Id,
          purchaseStock_BatchNo: stock.purchaseStock_BatchNo || stock.purchaseStock_BatchNo,
          purchaseStock_ExpDate: stock.purchaseStock_ExpDate || stock.purchaseStock_ExpDate,
          purchaseStock_IsAvailable: stock.purchaseStock_IsAvailable !== undefined ? stock.purchaseStock_IsAvailable : stock.purchaseStock_IsAvailable,
          purchaseStock_SalesPrice: stock.purchaseStock_SalesPrice || stock.purchaseStock_SalesPrice,
          purchaseStock_CostPrice: stock.purchaseStock_CostPrice || stock.purchaseStock_CostPrice,
          purchaseStock_IsBonus: stock.purchaseStock_IsBonus !== undefined ? stock.purchaseStock_IsBonus : stock.purchaseStock_IsBonus,
          purchaseStock_Branch_Id: stock.purchaseStock_Branch_Id || stock.purchaseStock_Branch_Id
        }));
      }
    }
    
    // Clear previous stock data when opening modal for a different product
    // This prevents showing stale data from previous product
    setForm(prev => {
      // If opening for a different product, clear its stock data temporarily
      const newSelectedStockData = { ...prev.selectedStockData };
      // Don't clear here - let the modal handle it based on productId change
      
      return {
        ...prev,
        showStockModal: true,
        currentStockProduct: {
          id: productId,
          name: productName,
          quantity: quantity || 1,
          itemIndex: itemIndex
        },
        preloadedStockItems: preloadedStock
      };
    });
  };

  const handleStockSelected = (stockData) => {
    const productId = form.currentStockProduct?.id;
    const itemIndex = form.currentStockProduct?.itemIndex;
    
    if (productId && stockData.length > 0) {
      // Extract stock IDs from selected stock data
      const stockIds = [];
      stockData.forEach(group => {
        group.stocks.forEach(stock => {
          stockIds.push(stock.purchaseStock_Id || stock.stockTransaction_StockId);
        });
      });
      
      // Calculate selected stock quantity
      const selectedQty = stockData.reduce((sum, group) => {
        if (group && typeof group.quantity === 'number') return sum + group.quantity;
        if (group && Array.isArray(group.stocks)) return sum + group.stocks.length;
        return sum;
      }, 0);
      
      // Update selectedStockData and modal quantity if opened from modal
      setForm(prev => {
        const updates = {
          ...prev,
          selectedStockData: {
            ...prev.selectedStockData,
            [productId]: stockData
          }
        };
        
        // If opened from modal (itemIndex is null), update modal quantity to match selected stock qty
        if (itemIndex === null && prev.selectedModalItems[productId]) {
          updates.selectedModalItems = {
            ...prev.selectedModalItems,
            [productId]: selectedQty
          };
        }
        
        return updates;
      });
      
      // For return mode, update entity item with stock data
      if (isReturnMode && itemIndex !== null) {
        setForm(prev => {
          const updatedEntityItems = [...prev.entityItems];
          if (updatedEntityItems[itemIndex]) {
            const item = updatedEntityItems[itemIndex];
            const newQuantity = stockIds.length;
            
            // Update item with stock data and quantity
            const totalPrice = newQuantity * (item.unitPrice || 0);
            const discountAmount = totalPrice * ((item.discount || 0) / 100);
            const amountAfterDiscount = totalPrice - discountAmount;
            const taxAmount = amountAfterDiscount * ((item.tax || 0) / 100);
            const netAmount = amountAfterDiscount + taxAmount;
            
            updatedEntityItems[itemIndex] = {
              ...item,
              stockData: stockIds.join(','),
              quantity: newQuantity,
              discountAmount: discountAmount,
              taxAmount: taxAmount,
              netAmount: netAmount
            };
          }
          
          // Calculate totals immediately with updated items
          let totalQty = 0;
          let totalAmount = 0;
          let totalDiscount = 0;
          let totalTax = 0;
          let totalCost = 0;
          
          updatedEntityItems.forEach(item => {
            const lineTotal = item.unitPrice * item.quantity;
            const discountAmount = item.discountAmount || 0;
            const taxAmount = item.taxAmount || 0;
            const costAmount = (item.cost || 0) * item.quantity;
            
            totalQty += item.quantity;
            totalAmount += lineTotal;
            totalDiscount += discountAmount;
            totalTax += taxAmount;
            totalCost += costAmount;
          });
          
          // Apply overall discount percentage
          const overallDiscountAmount = (totalAmount * (prev.discountPercentage || 0)) / 100;
          const finalTotalDiscount = totalDiscount + overallDiscountAmount;
          
          // Calculate initial net amount: amount - discount + tax
          const initialNetAmount = totalAmount - finalTotalDiscount + totalTax;
          
          // Apply additional overall discount percentage to initial net amount
          const additionalOverallDiscountAmount = (initialNetAmount * (prev.overallDiscountPercentage || 0)) / 100;
          const finalNetAmount = initialNetAmount - additionalOverallDiscountAmount;
          
          return {
            ...prev,
            entityItems: updatedEntityItems,
            Invoice_TotalQty: totalQty,
            Invoice_TotalAmount: totalAmount,
            Invoice_DisAmount: finalTotalDiscount,
            Invoice_TaxAmount: totalTax,
            Invoice_NetAmount: finalNetAmount,
            CostAmount: totalCost,
            TotalCost: totalCost,
            overallDiscountAmount: additionalOverallDiscountAmount,
          };
        });
      }
    }
  };

  // Stock details modal handlers
  const handleOpenStockDetailsModal = (productName, stockDetails) => {
    // Temporarily lower SweetAlert z-index to ensure stock modal appears on top
    const sweetAlertContainers = document.querySelectorAll('.swal2-container');
    sweetAlertContainers.forEach(container => {
      container.style.zIndex = '1000';
    });
    
    setForm(prev => ({
      ...prev,
      showStockDetailsModal: true,
      currentStockDetailsProduct: productName,
      currentStockDetails: stockDetails || []
    }));
  };

  const addSelectedModalItems = () => {
    const entityOptions = 
      form.modalEntityType === 'product' ? productOptions :
      form.modalEntityType === 'service' ? serviceOptions :
      form.modalEntityType === 'package' ? packageOptions :
      form.modalEntityType === 'packageSeries' ? packageSeriesOptions : [];

    // Track updated quantities for products (declared outside if block for scope)
    const updatedQuantities = {};

    // Enforce stock selection for products before adding
    if (form.modalEntityType === 'product') {
      const productsNeedingStock = [];
      
      // Only check products that are actually selected (have a valid quantity > 0)
      Object.entries(form.selectedModalItems)
        .filter(([entityId, quantity]) => {
          // Filter out undefined, null, empty string, or 0 values
          if (quantity === undefined || quantity === null || quantity === '') return false;
          const qty = parseInt(quantity);
          return !isNaN(qty) && qty > 0;
        })
        .forEach(([entityId, quantity]) => {
          const finalQuantity = parseInt(quantity) || 1;
          const stockData = form.selectedStockData[entityId];
          const productMeta = entityOptions.find(option => option.value == entityId);
          const productName = productMeta ? productMeta.label : `Product ${entityId}`;
          
          if (!stockData || stockData.length === 0) {
            productsNeedingStock.push(productName);
          } else {
            // Sum selected stock quantities for this product
            const selectedQty = stockData.reduce((sum, group) => {
              if (group && typeof group.quantity === 'number') return sum + group.quantity;
              if (group && Array.isArray(group.stocks)) return sum + group.stocks.length;
              return sum;
            }, 0);
            // Auto-update qty to match selected stock qty
            if (selectedQty !== finalQuantity) {
              updatedQuantities[entityId] = selectedQty;
            }
          }
        });
      
      // Update quantities if there are mismatches
      if (Object.keys(updatedQuantities).length > 0) {
        setForm(prev => ({
          ...prev,
          selectedModalItems: {
            ...prev.selectedModalItems,
            ...updatedQuantities
          }
        }));
      }
      
      // Only show alert if stock is not selected
      if (productsNeedingStock.length > 0) {
        Swal.fire(
          'Select Stock',
          `Please select stock for the highlighted products before adding.\n\n- Stock not selected: ${productsNeedingStock.join(', ')}`,
          'warning'
        );
        return;
      }
    }

    // Only process products that are actually selected (have a valid quantity > 0)
    // Use updated quantities if they were modified
    const quantitiesToUse = Object.keys(updatedQuantities || {}).length > 0 
      ? { ...form.selectedModalItems, ...updatedQuantities }
      : form.selectedModalItems;
    
    Object.entries(quantitiesToUse)
      .filter(([entityId, quantity]) => {
        // Filter out undefined, null, empty string, or 0 values
        if (quantity === undefined || quantity === null || quantity === '') return false;
        const qty = parseInt(quantity);
        return !isNaN(qty) && qty > 0;
      })
      .forEach(([entityId, quantity]) => {
        const finalQuantity = parseInt(quantity) || 1;
        const entityData = entityOptions.find(option => option.value == entityId);
        if (entityData) {
          // Check if this product has stock data
          const stockData = form.selectedStockData[entityId];
          if (stockData && stockData.length > 0) {
            // Add each stock group as separate items
            stockData.forEach(stockGroup => {
              addEntityItem(form.modalEntityType, entityData, stockGroup.quantity, stockGroup);
            });
          } else {
            // Add as regular product without stock
            addEntityItem(form.modalEntityType, entityData, finalQuantity);
          }
        }
      });

    closeEntityModal();
  };

  // Check if customer has memberships
  const [customerHasMembership, setCustomerHasMembership] = useState(false);

  // Customer membership modal functions
  const openMembershipModal = async (customerId) => {
    if (!customerId) return;
    
    try {
      // First check if customer has memberships before opening modal
      const memberships = await getCustomerMemberships({ customerId: customerId });
      
      // Set membership status for button visibility
      setCustomerHasMembership(memberships && memberships.length > 0);
      
      // Only open modal if customer has memberships
      if (memberships && memberships.length > 0) {
        setForm(prev => ({
          ...prev,
          showMembershipModal: true,
          selectedMembershipInstallments: {},
          customerMemberships: [],
          installmentNames: {},
          entityNames: {}
        }));
        // Transform the data to match our expected format
        const transformedMemberships = memberships.map(membership => ({
          membershipId: membership.membershipId,
          customerId: membership.customerId,
          branchId: membership.branchId,
          entity_Id: membership.entity_Id,
          entity_Type: membership.entity_Type,
          purchaseDate: membership.purchaseDate,
          installments: (membership.installments || []).map(installment => ({
            m_InstallmentId: installment.m_InstallmentId,
            membershipId: installment.membershipId,
            seriesId: installment.seriesId,
            installmentId: installment.installmentId,
            amount: installment.amount,
            paidAmount: installment.billedAmount || installment.paidAmount || 0
          }))
        }));

        setForm(prev => ({
          ...prev,
          customerMemberships: transformedMemberships
        }));

        // Fetch installment names for package series
        const installmentNames = {};
        for (const membership of transformedMemberships) {
          if (membership.entity_Type === 'SER' && membership.installments.length > 0) {
            try {
              const seriesData = await getSpaPackageSeries({ seriesId: membership.entity_Id });
              if (seriesData && seriesData.length > 0 && seriesData[0].installments) {
                seriesData[0].installments.forEach(installment => {
                  installmentNames[installment.installmentId] = installment.installmentName;
                });
              }
            } catch (error) {
              console.error('Error fetching installment names:', error);
            }
          }
        }

        setForm(prev => ({
          ...prev,
          installmentNames: installmentNames
        }));

        // Fetch entity names for packages and series
        const entityNames = {};
        for (const membership of transformedMemberships) {
          try {
            if (membership.entity_Type === 'PAC') {
              // Fetch package name
              const packageData = await getSpaPackages({ packageId: membership.entity_Id });
              if (packageData && packageData.length > 0) {
                entityNames[membership.entity_Id] = packageData[0].packageName_En || `Package ${membership.entity_Id}`;
              }
            } else if (membership.entity_Type === 'SER') {
              // Fetch series name
              const seriesData = await getSpaPackageSeries({ seriesId: membership.entity_Id });
              if (seriesData && seriesData.length > 0) {
                entityNames[membership.entity_Id] = seriesData[0].seriesName_En || `Series ${membership.entity_Id}`;
              }
            }
          } catch (error) {
            console.error('Error fetching entity name:', error);
            entityNames[membership.entity_Id] = `${membership.entity_Type === 'PAC' ? 'Package' : 'Series'} ${membership.entity_Id}`;
          }
        }

        setForm(prev => ({
          ...prev,
          entityNames: entityNames
        }));
      }
    } catch (error) {
      console.error('Error loading customer memberships:', error);
      Swal.fire("Error", "Failed to load customer memberships", "error");
    }
  };

  const closeMembershipModal = () => {
    setForm(prev => ({
      ...prev,
      showMembershipModal: false,
      selectedMembershipInstallments: {},
      // Keep customerMemberships for validation purposes
      installmentNames: {},
      entityNames: {}
    }));
  };

  // Function to manually open membership modal (for the button)
  const openMembershipModalManually = async () => {
    if (form.Invoice_CustomerId) {
      await openMembershipModal(form.Invoice_CustomerId);
    } else {
      Swal.fire("Warning", "Please select a customer first", "warning");
    }
  };

  const handleMembershipInstallmentCheck = (installmentId, checked) => {
    setForm(prev => ({
      ...prev,
      selectedMembershipInstallments: {
        ...prev.selectedMembershipInstallments,
        [installmentId]: {
          ...prev.selectedMembershipInstallments[installmentId],
          checked: checked,
          // Clear validation error when unchecking
          validationError: checked ? prev.selectedMembershipInstallments[installmentId]?.validationError : ''
        }
      }
    }));
  };

  const handleMembershipInstallmentAmountChange = (installmentId, amount) => {
    // Find the installment to get its remaining amount for validation
    const installment = form.customerMemberships
      .flatMap(membership => membership.installments)
      .find(inst => inst.installmentId === installmentId);
    
    const remainingAmount = installment ? Math.round((installment.amount - installment.paidAmount) * 100) / 100 : 0;
    
    // Allow empty string, but validate when there's a value
    let validatedAmount = amount;
    let validationError = '';
    
    if (amount !== '' && amount !== null && amount !== undefined && !isNaN(amount)) {
      const inputAmount = parseFloat(amount);
      // Validate that input amount doesn't exceed remaining amount
      if (inputAmount > remainingAmount) {
        validationError = `Amount cannot exceed remaining balance of ${remainingAmount.toFixed(2)}`;
        validatedAmount = amount; // Keep the original input to show the error
      } else {
        validatedAmount = inputAmount;
      }
    }
    
    setForm(prev => ({
      ...prev,
      selectedMembershipInstallments: {
        ...prev.selectedMembershipInstallments,
        [installmentId]: {
          ...prev.selectedMembershipInstallments[installmentId],
          amount: validatedAmount,
          validationError: validationError
        }
      }
    }));
  };

  const addSelectedMembershipInstallments = async () => {
    // Check for validation errors first
    const hasValidationErrors = Object.values(form.selectedMembershipInstallments).some(data => 
      data.checked && data.validationError
    );
    
    if (hasValidationErrors) {
      Swal.fire("Validation Error", "Please fix the validation errors before proceeding.", "error");
      return;
    }
    
    for (const [installmentId, data] of Object.entries(form.selectedMembershipInstallments)) {
      if (data.checked && data.amount > 0) {
        // Find the membership and installment details
        const membership = form.customerMemberships.find(m => 
          m.installments.some(inst => inst.installmentId.toString() === installmentId)
        );
        
        if (membership) {
          const installment = membership.installments.find(inst => 
            inst.installmentId.toString() === installmentId
          );
          
          if (installment) {
            // Determine entity type and create appropriate entity data
            const entityType = membership.entity_Type === 'PAC' ? 'package' : 'packageSeries';
            const entityId = membership.entity_Id;
            
            // Find the entity name and get parent entity data
            let entityName = '';
            let parentEntityData = null;
            if (entityType === 'package') {
              const pkg = packages.find(p => p.packageId === entityId);
              entityName = pkg?.packageName_En || `Package ${entityId}`;
              parentEntityData = pkg; // Use package data for discount/tax
            } else {
              const series = packageSeries.find(s => s.seriesId === entityId);
              entityName = series?.seriesName_En || `Package Series ${entityId}`;
              parentEntityData = series; // Use package series data for discount/tax
            }
            
            // Get installment name
            const installmentName = form.installmentNames[installmentId] || `Installment ${installmentId}`;
            
            // Create entity data object
            const entityData = {
              value: entityId,
              label: `${entityName} - ${installmentName}`,
              installmentId: installmentId,
              installmentName: installmentName,
              originalAmount: installment.amount,
              m_InstallmentId: installment.m_InstallmentId // Add the membership installment ID
            };
            
            // Add to entity items with quantity 1 and custom unit price, passing parent entity data
            await addEntityItemWithCustomPrice(entityType, entityData, 1, data.amount, parentEntityData);
          }
        }
      }
    }

    closeMembershipModal();
  };


  // Dropdown helper function
  const toggleDropdown = (itemId) => {
    setForm(prev => ({
      ...prev,
      dropdownOpen: {
        ...prev.dropdownOpen,
        [itemId]: !prev.dropdownOpen[itemId]
      }
    }));
  };

  // Helper functions
  const getCustomerName = (customerId) => {
    if (!customerId) return "N/A";
    const customer = customers.find(c => c.customer_Id === customerId);
    return customer ? customer.customer_NameEn : "N/A";
  };

  const getBusinessName = (businessId) => {
    const business = businesses.find(b => b.business_Id === businessId);
    return business ? business.business_NameEn : "N/A";
  };

  const getBranchName = (branchId) => {
    if (!branchId) return "N/A";
    const branch = branches.find(b => b.branch_Id === branchId);
    return branch ? branch.branch_NameEn : "N/A";
  };

  const getPaymentMethodName = (method) => {
    if (!method) return "N/A";
    switch (method) {
      case "ca": return "Cash";
      case "cc": return "Credit Card";
      case "on": return "Online";
      default: return "N/A";
    }
  };

  const getInvoiceTypeName = (type) => {
    if (!type) return "N/A";
    switch (type.toLowerCase()) {
      case "cr": return "Credit";
      case "ca": return "Cash";
      default: return type.toUpperCase();
    }
  };

  // Filter invoices based on isReturn flag
  const filteredInvoices = useMemo(() => {
    if (invoiceListType === 'return') {
      return invoices.filter(inv => inv.isReturn === true || inv.IsReturn === true);
    } else {
      return invoices.filter(inv => !inv.isReturn && !inv.IsReturn);
    }
  }, [invoices, invoiceListType]);

  // Calculate pagination based on filtered invoices
  const filteredTotalCount = filteredInvoices.length;
  const totalPages = Math.ceil(filteredTotalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredInvoices.slice(startIndex, endIndex);
  
  // Reset to page 1 when switching between invoice types
  useEffect(() => {
    setPage(1);
  }, [invoiceListType]);

  // Options for dropdowns
  const customerOptions = useMemo(() => {
    return customers.map(customer => ({
      value: customer.customer_Id,
      label: customer.customer_NameEn
    }));
  }, [customers]);

  const businessOptions = useMemo(() => {
    return businesses.map(business => ({
      value: business.business_Id,
      label: business.business_NameEn
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    // Filter branches based on selected business
    const filteredBranches = form.Invoice_BusinessId 
      ? branches.filter(branch => branch.branch_Buisness_Id === form.Invoice_BusinessId)
      : branches;
    
    return filteredBranches.map(branch => ({
      value: branch.branch_Id,
      label: branch.branch_NameEn
    }));
  }, [branches, form.Invoice_BusinessId]);

  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.product_Id,
      label: product.product_NameEn
    }));
  }, [products]);

  const serviceOptions = useMemo(() => {
    return services.map(service => ({
      value: service.spaService_Id,
      label: service.spaServiceName_En
    }));
  }, [services]);

  const packageOptions = useMemo(() => {
    return packages.map(pkg => ({
      value: pkg.packageId,
      label: pkg.packageName_En
    }));
  }, [packages]);

  const packageSeriesOptions = useMemo(() => {
    return packageSeries.map(series => ({
      value: series.seriesId,
      label: series.seriesName_En
    }));
  }, [packageSeries]);

  // Invoice Print Modal Component
  const InvoicePrintModal = ({ isOpen, toggle, invoiceData }) => {
    if (!invoiceData || !invoiceData.header) return null;

    const printRef = useRef(null);

    const header = invoiceData.header;
    const details = invoiceData.details || [];
    const taxes = invoiceData.taxes || [];

    // Extract values with fallback for camelCase/PascalCase
    const invoiceNo = header.invoice_No || header.Invoice_No;
    const invoiceDate = header.invoice_Date || header.Invoice_Date;
    const customerName = header.customer_NameEn || header.Customer_NameEn;
    const customerAddress = header.customer_AddressEn || header.Customer_AddressEn;
    const customerTRN = header.customer_TRN || header.Customer_TRN;
    const businessName = header.business_NameEn || header.Business_NameEn;
    const branchName = header.branch_NameEn || header.Branch_NameEn;
    const businessTRN = header.business_TRN || header.Business_TRN;
    const companyLogoPath = header.company_LogoPath || header.Company_LogoPath;
    const invoiceType = header.invoice_Type || header.Invoice_Type;
    const cashAmount = header.cashAmount || header.CashAmount || 0;
    const cashlessAmount = header.cashlessAmount || header.CashlessAmount || 0;
    const totalVAT = header.invoice_TaxAmount || header.Invoice_TaxAmount || 0;
    const overallDiscount = header.overallDiscountAmount || header.OverallDiscountAmount || 0;
    const grandTotal = header.invoice_NetAmount || header.Invoice_NetAmount || 0;

    const formatAmount = (amount) => {
      return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        return 'N/A';
      }
    };

    const handlePrint = () => {
      if (!printRef.current) return;

      const now = new Date();
      const formattedDate = now.toISOString().replace(/[-:]/g, "").replace(/\..+/, "");
      const fileName = `Invoice_${invoiceNo}_${formattedDate}`;

      const element = printRef.current;

      const options = {
        margin: [10, 10, 10, 10],
        filename: `${fileName}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
        },
        jsPDF: {
          unit: "pt",
          format: "a4",
          orientation: "portrait",
        },
      };

      html2pdf().set(options).from(element).save();
    };

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          Invoice - {header.invoice_No || header.Invoice_No}
        </ModalHeader>
        <ModalBody>
          <div ref={printRef} className="border p-4">
            <div className="text-center mb-4">
              {companyLogoPath && (
                <div className="mb-3">
                  <img 
                    src={`${BASE_URL}${companyLogoPath.startsWith('/') ? companyLogoPath : '/' + companyLogoPath}`} 
                    alt="Company Logo" 
                    style={{ 
                      maxHeight: '60px', 
                      maxWidth: '200px', 
                      objectFit: 'contain' 
                    }} 
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <h4><strong>{(businessName || 'COMPANY').toUpperCase()}</strong></h4>
              {branchName && <h5>{branchName}</h5>}
              <h4>TAX INVOICE</h4>
              {businessTRN && businessTRN.trim() !== '' && (
                <p className="mb-0"><strong>TRN:</strong> {businessTRN}</p>
              )}
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <table className="table table-bordered table-sm">
                  <tbody>
                    <tr>
                      <td><strong>Customer Name:</strong> {customerName || 'N/A'}</td>
                      <td><strong>Date:</strong> {formatDate(invoiceDate)}</td>
                    </tr>
                    {customerTRN && customerTRN.trim() !== '' && (
                      <tr>
                        <td colSpan="2"><strong>TRN:</strong> {customerTRN}</td>
                      </tr>
                    )}
                    {customerAddress && customerAddress.trim() !== '' && (
                      <tr>
                        <td colSpan="2"><strong>Address:</strong> {customerAddress}</td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="2"><strong>Invoice No.:</strong> {invoiceNo}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <table className="table table-bordered table-sm">
                  <thead>
                    <tr>
                      <th>S.No / الرقم</th>
                      <th>Description / الوصف</th>
                      <th className="text-end">Price / السعر</th>
                      <th className="text-end">Qty / الكمية</th>
                      <th className="text-end">Disc. / الخصم</th>
                      <th className="text-end">Net / الصافي</th>
                      <th className="text-end">VAT / ضريبة القيمة المضافة</th>
                      <th className="text-end">Total / الإجمالي</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((detail, index) => (
                      <tr key={detail.detail_Id || detail.Detail_Id}>
                        <td>{index + 1}</td>
                        <td>{detail.entityName || detail.EntityName || 'Unknown Item'}</td>
                        <td className="text-end">{formatAmount(detail.price || detail.Price || 0)}</td>
                        <td className="text-end">{Math.round(detail.quantity || detail.Quantity || 0)}</td>
                        <td className="text-end">{formatAmount(detail.disAmount || detail.DisAmount || 0)}</td>
                        <td className="text-end">{formatAmount(detail.netAmount || detail.NetAmount || 0)}</td>
                        <td className="text-end">{formatAmount(detail.taxAmt || detail.TaxAmt || 0)}</td>
                        <td className="text-end">{formatAmount(detail.netAmount || detail.NetAmount || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tax Summary Section */}
            {taxes && taxes.length > 0 && (
              <div className="row mt-2">
                <div className="col-12">
                  {(() => {
                    const taxSummary = {};
                    taxes.forEach(tax => {
                      const taxName = tax.tax_NameEn || tax.Tax_NameEn || 'Tax';
                      const taxPerc = tax.tax_Perc || tax.Tax_Perc || 0;
                      const key = `${taxName}_${taxPerc}`;
                      
                      if (!taxSummary[key]) {
                        taxSummary[key] = {
                          name: taxName,
                          percentage: taxPerc
                        };
                      }
                    });
                    
                    const taxLabels = Object.values(taxSummary).map(tax => `${tax.name} ${tax.percentage}%`);
                    const taxSummaryText = taxLabels.join(', ');
                    
                    return (
                      <p className="mb-2"><strong>Tax Summary / ملخص الضرائب:</strong> {taxSummaryText}</p>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Summary Totals */}
            <div className="row mt-2">
              <div className="col-12">
                <table className="table table-sm table-bordered">
                  <tbody>
                    {overallDiscount > 0 && (
                      <tr>
                        <td colSpan="5" className="text-end"><strong>Additional Discount / خصم إضافي:</strong></td>
                        <td className="text-end"><strong>{formatAmount(overallDiscount)}</strong></td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="5" className="text-end"><strong>Grand Total / الإجمالي الكلي:</strong></td>
                      <td className="text-end"><strong>{formatAmount(grandTotal)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-12">
                <table className="table table-bordered table-sm">
                  <tbody>
                    {cashAmount > 0 && (
                      <tr>
                        <td><strong>Cash Amount:</strong> AED {formatAmount(cashAmount)}</td>
                      </tr>
                    )}
                    {cashlessAmount > 0 && (
                      <tr>
                        <td><strong>Cashless Amount:</strong> AED {formatAmount(cashlessAmount)}</td>
                      </tr>
                    )}
                    {invoiceType === 'cr' && (
                      <tr>
                        <td><strong>Credit</strong></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-6">
                <p className="mb-1"><strong>Received By:</strong> {customerName || 'N/A'}</p>
              </div>
              <div className="col-6 text-end">
                <p className="mb-1"><strong>Prepared By:</strong> Jijo Thomas</p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handlePrint}>
            <Printer size={16} className="me-2" />
            Print
          </Button>
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <style>
        {`
          .swal-wide {
            max-width: 90% !important;
            width: 90% !important;
          }
          .swal-wide .swal2-html-container {
            max-height: 70vh;
            overflow-y: auto;
          }
          .swal-wide table {
            font-size: 0.875rem;
          }
          .swal-wide .table th,
          .swal-wide .table td {
            padding: 0.5rem;
            vertical-align: middle;
          }
        `}
      </style>
      <Container fluid>
        <Breadcrumb title="Billing Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <Receipt size={16} />
                        <span>Add Invoice</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Invoice List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      {isReadOnly && (
                        <div className="alert alert-info mb-4" role="alert">
                          <i className="fas fa-eye me-2"></i>
                          <strong>Read-Only Mode:</strong> You are viewing invoice details. No changes can be made.
                        </div>
                      )}
                                              <Form onSubmit={(e) => { e.preventDefault(); if (!isReadOnly) handleSave(); }}>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Invoice_BusinessId">Company <span className="text-danger">*</span></Label>
                              <Select
                                value={businessOptions.find(option => option.value === form.Invoice_BusinessId) || null}
                                onChange={(selected) => setForm({ 
                                  ...form, 
                                  Invoice_BusinessId: selected?.value || null,
                                  Invoice_BranchId: null // Reset branch when business changes
                                })}
                                options={businessOptions}
                                placeholder="Select Company"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                required
                                isDisabled={isReadOnly || isReturnMode || (localStorage.getItem("userCompanyId") && form.Invoice_BusinessId === parseInt(localStorage.getItem("userCompanyId")))}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Invoice_BranchId">Branch</Label>
                              <Select
                                value={branchOptions.find(option => option.value === form.Invoice_BranchId) || null}
                                onChange={(selected) => setForm({ ...form, Invoice_BranchId: selected?.value || null })}
                                options={branchOptions}
                                placeholder="Select Branch"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                isDisabled={isReadOnly || isReturnMode || !form.Invoice_BusinessId}
                                noOptionsMessage={() => !form.Invoice_BusinessId ? "Please select a company first" : "No branches found"}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Invoice_No">Invoice Number</Label>
                              <Input
                                type="text"
                                id="Invoice_No"
                                value={form.Invoice_No ? String(form.Invoice_No) : ''}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                                placeholder="Auto-generated"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Invoice_Date">Date <span className="text-danger">*</span></Label>
                              <DatePicker
                                selected={form.Invoice_Date}
                                onChange={(date) => setForm({ ...form, Invoice_Date: date })}
                                className="form-control"
                                dateFormat="dd/MM/yyyy"
                                required
                                readOnly={isReadOnly}
                                disabled={isReadOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Invoice_CustomerId">Customer <span className="text-danger">*</span></Label>
                              <div className="d-flex gap-2">
                                <div style={{ flex: 1 }}>
                                  <Select
                                    value={selectedCustomerOption}
                                    onChange={(selected) => {
                                      setSelectedCustomerOption(selected);
                                      setForm({ ...form, Invoice_CustomerId: selected?.value || null });
                                      // Reset membership status when customer changes
                                      setCustomerHasMembership(false);
                                      // Open membership modal when customer is selected
                                      if (selected?.value) {
                                        openMembershipModal(selected.value);
                                      }
                                    }}
                                    options={customerSearchOptions}
                                    placeholder="Type at least 3 characters to search customers..."
                                    styles={selectStyles}
                                    isClearable
                                    isSearchable
                                    required
                                    isDisabled={isReadOnly || isReturnMode}
                                    isLoading={customerSearchLoading}
                                    noOptionsMessage={() => "Type at least 3 characters to search"}
                                    loadingMessage={() => "Searching customers..."}
                                    onInputChange={(inputValue) => {
                                      // Clear previous timeout
                                      if (searchTimeout) {
                                        clearTimeout(searchTimeout);
                                      }
                                      
                                      // Set new timeout
                                      const timeout = setTimeout(() => {
                                        searchCustomersManually(inputValue);
                                      }, 300);
                                      setSearchTimeout(timeout);
                                      
                                      return inputValue;
                                    }}
                                  />
                                </div>
                                {selectedCustomerOption?.value && (
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => setShowCustomerProfile(true)}
                                    disabled={isReadOnly}
                                    title="View Customer Profile"
                                  >
                                    <User size={16} />
                                  </Button>
                                )}
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => setShowQuickAddCustomer(true)}
                                  disabled={isReadOnly}
                                  title="Quick Add Customer"
                                >
                                  <i className="fas fa-plus"></i>
                                </Button>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Customer Advance Balance and Outstanding Credit Info */}
                        {(customerAdvanceBalance || customerOutstandingCredit) && (
                          <Row className="mb-3">
                            <Col md={12}>
                              <div className="d-flex gap-3 flex-wrap">
                                {customerOutstandingCredit && (
                                  <Card className="border-warning" style={{ flex: '1', minWidth: '200px' }}>
                                    <CardBody className="p-3">
                                      <div className="d-flex align-items-center">
                                        <div className="me-3">
                                          <i className="fas fa-file-invoice-dollar text-warning" style={{ fontSize: '24px' }}></i>
                                        </div>
                                        <div>
                                          <h6 className="mb-1 text-muted" style={{ fontSize: '12px' }}>Outstanding Credit</h6>
                                          <h5 className="mb-0 text-warning">
                                            {loadingCustomerInfo ? (
                                              <ClipLoader size={16} color="#ffc107" />
                                            ) : (
                                              `AED ${(customerOutstandingCredit.totalOutstanding || customerOutstandingCredit.TotalOutstanding || 0).toFixed(2)}`
                                            )}
                                          </h5>
                                        </div>
                                      </div>
                                    </CardBody>
                                  </Card>
                                )}
                                {customerAdvanceBalance && (
                                  <Card className="border-success" style={{ flex: '1', minWidth: '200px' }}>
                                    <CardBody className="p-3">
                                      <div className="d-flex align-items-center">
                                        <div className="me-3">
                                          <i className="fas fa-wallet text-success" style={{ fontSize: '24px' }}></i>
                                        </div>
                                        <div>
                                          <h6 className="mb-1 text-muted" style={{ fontSize: '12px' }}>Available Advance</h6>
                                          <h5 className="mb-0 text-success">
                                            {loadingCustomerInfo ? (
                                              <ClipLoader size={16} color="#28a745" />
                                            ) : (
                                              `AED ${(customerAdvanceBalance.availableAdvance || customerAdvanceBalance.AvailableAdvance || 0).toFixed(2)}`
                                            )}
                                          </h5>
                                        </div>
                                      </div>
                                    </CardBody>
                                  </Card>
                                )}
                              </div>
                            </Col>
                          </Row>
                        )}

                        {/* New Entity Selection Section */}
                        {form.Invoice_BusinessId && form.Invoice_BranchId ? (
                          <Row className="mb-4">
                            <Col md={12}>
                              <Card className="border-info">
                                <CardBody>
                                  <h6 className="card-title text-info mb-3">Add Items to Invoice</h6>
                                  {/* Entity type selection buttons */}
                                  <Row className="mb-3">
                                    <Col md={12}>
                                      <div className="d-flex gap-3">
                                        <Button
                                          color="primary"
                                          size="lg"
                                          onClick={() => openEntityModal('product')}
                                          disabled={isReadOnly || isReturnMode}
                                        >
                                          <i className="fas fa-box me-2"></i>
                                          Products
                                        </Button>
                                        <Button
                                          color="info"
                                          size="lg"
                                          onClick={() => openEntityModal('service')}
                                          disabled={isReadOnly || isReturnMode}
                                        >
                                          <i className="fas fa-spa me-2"></i>
                                          Services
                                        </Button>
                                        <Button
                                          color="warning"
                                          size="lg"
                                          onClick={() => openEntityModal('package')}
                                          disabled={isReadOnly || isReturnMode}
                                        >
                                          <i className="fas fa-gift me-2"></i>
                                          Packages
                                        </Button>
                                        <Button
                                          color="secondary"
                                          size="lg"
                                          onClick={() => openEntityModal('packageSeries')}
                                          disabled={isReadOnly || isReturnMode}
                                        >
                                          <i className="fas fa-layer-group me-2"></i>
                                          Package Series
                                        </Button>
                                        {customerHasMembership && (
                                          <Button
                                            color="success"
                                            size="lg"
                                            onClick={openMembershipModalManually}
                                            disabled={isReadOnly || isReturnMode}
                                          >
                                            <i className="fas fa-id-card me-2"></i>
                                            Installments
                                          </Button>
                                        )}
                                        {isReturnMode && (
                                          <div className="ms-auto align-self-center">
                                            <span className="badge bg-warning text-dark">
                                              <RotateCcw size={14} className="me-1" />
                                              Sales Return Mode
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </Col>
                                  </Row>
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>
                        ) : (
                          <div className="alert alert-warning mb-4">
                            Please select a company and branch to add items.
                          </div>
                        )}

                        {/* Entity Items Display */}
                        {form.entityItems.length > 0 && (
                          <Row className="mb-4">
                            <Col md={12} style={{ marginLeft: '-15px', paddingLeft: '0', paddingRight: '0' }}>
                              <Card>
                                <CardBody>
                                  <h6 className="card-title mb-3">Selected Items</h6>
                                  {/* Vertical Scrollable Container */}
                                                                     <div 
                                     style={{ 
                                       maxHeight: '400px', 
                                       overflowY: 'auto', 
                                       overflowX: 'hidden',
                                       border: '1px solid #dee2e6',
                                       borderRadius: '6px',
                                       padding: '10px',
                                       width: '100%',
                                       minWidth: '1200px'
                                     }}
                                   >
                                    {form.entityItems.map((item, index) => (
                                      <div key={item.id} className="border p-2 mb-2 rounded bg-light">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', width: '100%' }}>
                                          {/* Item Type and Name */}
                                          <div style={{ flex: '0 0 200px' }}>
                                            <div className="small fw-bold text-primary">{(item.type || '').charAt(0).toUpperCase() + (item.type || '').slice(1)}</div>
                                            <div className="small" style={{ lineHeight: '1.2', wordWrap: 'break-word' }} title={item.entityName}>
                                              {item.entityName}
                                            </div>
                                          </div>
                                          
                                                                                     {/* Qty */}
                                           <div style={{ flex: '0 0 60px' }}>
                                             <Label className="small mb-1">Qty:</Label>
                                             <Input
                                               type="number"
                                               min="1"
                                               value={item.quantity}
                                               onChange={(e) => updateEntityItemQuantity(item.id, e.target.value)}
                                               onFocus={(e) => e.target.select()}
                                               size="sm"
                                               style={{ fontSize: '11px', padding: '3px 5px', width: '50px' }}
                                               readOnly={isReadOnly || item.m_InstallmentId || isReturnMode || (item.type === 'product' && item.stockData)}
                                               disabled={isReadOnly || item.m_InstallmentId || isReturnMode || (item.type === 'product' && item.stockData)}
                                             />
                                           </div>
                                          
                                          {/* Unit Price */}
                                          <div style={{ flex: '0 0 90px' }}>
                                            <Label className="small mb-1">Unit Price:</Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              value={formatNumericValue(item.unitPrice)}
                                              onChange={(e) => updateEntityItemUnitPrice(item.id, e.target.value)}
                                              onFocus={(e) => e.target.select()}
                                              size="sm"
                                              placeholder="0.00"
                                              style={{ 
                                                fontSize: '11px', 
                                                padding: '3px 5px',
                                                borderColor: item.validationError ? '#dc3545' : undefined,
                                                backgroundColor: isReturnMode ? '#f8f9fa' : 'white'
                                              }}
                                              readOnly={isReadOnly || isReturnMode}
                                              disabled={isReadOnly || isReturnMode}
                                            />
                                            {item.validationError && (
                                              <div className="text-danger" style={{ fontSize: '10px', marginTop: '2px' }}>
                                                {item.validationError}
                                              </div>
                                            )}
                                          </div>
                                          
                                          
                                          {/* Total Price */}
                                          <div style={{ flex: '0 0 100px' }}>
                                            <Label className="small mb-1">Total Price:</Label>
                                            <Input
                                              type="text"
                                              value={((item.quantity * (item.unitPrice ?? 0)) || 0).toFixed(2)}
                                              readOnly
                                              size="sm"
                                              style={{ backgroundColor: '#f8f9fa', fontSize: '11px', padding: '3px 5px' }}
                                            />
                                          </div>
                                          
                                                                                     {/* Dis(%) */}
                                           <div style={{ flex: '0 0 50px' }}>
                                             <Label className="small mb-1">Dis(%):</Label>
                                             <Input
                                               type="number"
                                               step="0.01"
                                               min="0"
                                               max="100"
                                               value={formatNumericValue(item.discount)}
                                               onChange={(e) => updateEntityItemDiscount(item.id, e.target.value)}
                                               onFocus={(e) => e.target.select()}
                                               size="sm"
                                               style={{ fontSize: '11px', padding: '3px 5px', width: '40px' }}
                                               readOnly={isReadOnly || isReturnMode}
                                               disabled={isReadOnly || isReturnMode}
                                             />
                                           </div>
                                          
                                          {/* Dis Amt */}
                                          <div style={{ flex: '0 0 85px' }}>
                                            <Label className="small mb-1">Dis Amt:</Label>
                                            <Input
                                              type="text"
                                              value={(item.discountAmount || 0).toFixed(2)}
                                              readOnly
                                              size="sm"
                                              style={{ backgroundColor: '#f8f9fa', fontSize: '11px', padding: '3px 5px' }}
                                            />
                                          </div>
                                          
                                                                                     {/* Tax(%) */}
                                           <div style={{ flex: '0 0 50px' }}>
                                             <Label className="small mb-1">Tax(%):</Label>
                                             <div
                                               style={{ 
                                                 position: 'relative',
                                                 backgroundColor: '#f8f9fa', 
                                                 fontSize: '11px', 
                                                 padding: '3px 5px', 
                                                 width: '40px',
                                                 border: '1px solid #ced4da',
                                                 borderRadius: '4px',
                                                 cursor: item.taxBreakdown && item.taxBreakdown.length > 1 ? 'help' : 'default',
                                                 textAlign: 'center',
                                                 lineHeight: '1.2',
                                                 whiteSpace: 'nowrap'
                                               }}
                                               title={item.taxBreakdown && item.taxBreakdown.length > 1 ? 
                                                 `Tax Breakdown:\n${item.taxBreakdown.map(tax => `${tax.name}: ${tax.percentage}%`).join('\n')}` : 
                                                 item.taxBreakdown && item.taxBreakdown.length === 1 ? 
                                                 `${item.taxBreakdown[0].name}: ${item.taxBreakdown[0].percentage}%` : 
                                                 'No tax'
                                               }
                                             >
                                               {(parseFloat(item.tax) || 0).toFixed(1)}
                                               {item.taxBreakdown && item.taxBreakdown.length > 1 && (
                                                 <span style={{ fontSize: '8px', color: '#6c757d' }}>+</span>
                                               )}
                                             </div>
                                           </div>
                                          
                                          {/* Tax Amount */}
                                          <div style={{ flex: '0 0 75px' }}>
                                            <Label className="small mb-1">Tax Amount:</Label>
                                            <Input
                                              type="text"
                                              value={(item.taxAmount || 0).toFixed(2)}
                                              readOnly
                                              size="sm"
                                              style={{ backgroundColor: '#f8f9fa', fontSize: '11px', padding: '3px 5px' }}
                                            />
                                          </div>
                                          
                                          {/* Net Amount */}
                                          <div style={{ flex: '0 0 100px' }}>
                                            <Label className="small mb-1">Net Amount:</Label>
                                            <Input
                                              type="text"
                                              value={(item.netAmount || 0).toFixed(2)}
                                              readOnly
                                              size="sm"
                                              style={{ backgroundColor: '#e8f5e8', fontSize: '11px', padding: '3px 5px', fontWeight: 'bold' }}
                                            />
                                          </div>
                                          
                                          {/* Salesman - Only for Products */}
                                          {item.type === 'product' && (
                                            <div style={{ flex: '0 0 100px' }}>
                                              <Label className="small mb-1">Salesman:</Label>
                                              <Select
                                                value={employees.find(emp => emp.employee_Id === item.salesmanId) ? 
                                                  { value: item.salesmanId, label: employees.find(emp => emp.employee_Id === item.salesmanId)?.employee_NameEn } : 
                                                  null}
                                                onChange={(option) => updateEntityItemSalesman(item.id, option?.value || 0)}
                                                options={employees.map(emp => ({
                                                  value: emp.employee_Id,
                                                  label: emp.employee_NameEn
                                                }))}
                                                placeholder="Select"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                  ...selectStyles,
                                                  control: (provided, state) => ({
                                                    ...selectStyles.control(provided, state),
                                                    minHeight: '28px',
                                                    fontSize: '11px',
                                                    padding: '2px 5px'
                                                  }),
                                                  menu: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999
                                                  }),
                                                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                }}
                                                isClearable={false}
                                                isSearchable
                                                isDisabled={isReadOnly || isReturnMode}
                                              />
                                            </div>
                                          )}
                                          
                                          {/* Stock Selection Button - Only for Products in Return Mode */}
                                          {item.type === 'product' && isReturnMode && (
                                            <div style={{ flex: '0 0 80px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', minWidth: '80px', paddingTop: '20px' }}>
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleOpenStockModal(item.entityId, item.entityName, item.quantity, form.entityItems.findIndex(i => i.id === item.id))}
                                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                                title="Select Stock to Return"
                                                disabled={isReadOnly}
                                              >
                                                <Package size={14} className="me-1" />
                                                Stock
                                              </Button>
                                            </div>
                                          )}
                                          
                                                                                                                                {/* Remove Button - Same row, far right */}
                                           <div style={{ flex: '0 0 40px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', minWidth: '40px', paddingTop: '20px' }}>
                                             <Button
                                               color="danger"
                                               size="sm"
                                               onClick={() => removeEntityItem(item.id)}
                                               style={{ fontSize: '12px', padding: '2px 4px', width: '25px', height: '25px' }}
                                               title="Remove Item"
                                               disabled={isReadOnly}
                                             >
                                               ×
                                             </Button>
                                           </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Add Button with Dropdown - Moved below scrollable container */}
                                  {!isReturnMode && (
                                    <div className="mt-3">
                                      <Dropdown 
                                        isOpen={form.addButtonDropdownOpen || false} 
                                        toggle={() => !isReadOnly && form.Invoice_BusinessId && form.Invoice_BranchId && setForm(prev => ({ ...prev, addButtonDropdownOpen: !prev.addButtonDropdownOpen }))}
                                        direction="down"
                                        disabled={isReadOnly || !form.Invoice_BusinessId || !form.Invoice_BranchId}
                                      >
                                        <DropdownToggle 
                                          color="success" 
                                          className="waves-effect waves-light"
                                          style={{ 
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                          }}
                                        >
                                          <i className="fas fa-plus me-2"></i>Add Item <i className="fas fa-chevron-down ms-2"></i>
                                        </DropdownToggle>
                                        <DropdownMenu>
                                          <DropdownItem onClick={() => openEntityModal('product')}>
                                            <i className="fas fa-box me-2 text-primary"></i>Add Product
                                          </DropdownItem>
                                          <DropdownItem onClick={() => openEntityModal('service')}>
                                            <i className="fas fa-spa me-2 text-info"></i>Add Service
                                          </DropdownItem>
                                          <DropdownItem onClick={() => openEntityModal('package')}>
                                            <i className="fas fa-gift me-2 text-warning"></i>Add Package
                                          </DropdownItem>
                                          <DropdownItem onClick={() => openEntityModal('packageSeries')}>
                                            <i className="fas fa-layer-group me-2 text-secondary"></i>Add Package Series
                                          </DropdownItem>
                                        </DropdownMenu>
                                      </Dropdown>
                                    </div>
                                  )}
                                </CardBody>
                              </Card>
                           </Col>
                        </Row>
                        )}

                                                 {/* Entity Totals */}
                         {(form.entityItems.filter(item => item.type === 'product').length > 0 || form.entityItems.filter(item => item.type === 'service').length > 0 || form.entityItems.filter(item => item.type === 'package').length > 0 || form.entityItems.filter(item => item.type === 'packageSeries').length > 0 || form.entityItems.filter(item => item.m_InstallmentId).length > 0) && (
                           <Row className="mb-4">
                             <Col md={12}>
                               <Card className="border-primary">
                                 <CardBody>
                                   <h6 className="card-title text-primary mb-3">Entity Totals</h6>
                                   <Row>
                                     {form.entityItems.filter(item => item.type === 'product').length > 0 && (
                                       <Col md={3}>
                                         <div className="border rounded p-2">
                                           <h6 className="text-success mb-2">Products</h6>
                                           {(() => {
                                             const totals = getEntityTotalsFromItems('product');
                                             return (
                                               <>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Qty:</span>
                                                   <span className="fw-bold small">{totals.totalQty || 0}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Amount:</span>
                                                   <span className="fw-bold small">{(totals.totalAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveDiscountPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalDiscountAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveTaxPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalTaxAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Net Amount:</span>
                                                   <span className="fw-bold small text-primary">{(totals.netAmount || 0).toFixed(2)}</span>
                                                 </div>
                                               </>
                                             );
                                           })()}
                                         </div>
                                       </Col>
                                     )}
                                     {form.entityItems.filter(item => item.type === 'service').length > 0 && (
                                       <Col md={3}>
                                         <div className="border rounded p-2">
                                           <h6 className="text-info mb-2">Services</h6>
                                           {(() => {
                                             const totals = getEntityTotalsFromItems('service');
                                             return (
                                               <>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Qty:</span>
                                                   <span className="fw-bold small">{totals.totalQty || 0}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Amount:</span>
                                                   <span className="fw-bold small">{(totals.totalAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveDiscountPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalDiscountAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveTaxPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalTaxAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Net Amount:</span>
                                                   <span className="fw-bold small text-primary">{(totals.netAmount || 0).toFixed(2)}</span>
                                                 </div>
                                               </>
                                             );
                                           })()}
                                         </div>
                                       </Col>
                                     )}
                                     {form.entityItems.filter(item => item.type === 'package').length > 0 && (
                                       <Col md={3}>
                                         <div className="border rounded p-2">
                                           <h6 className="text-warning mb-2">Packages</h6>
                                           {(() => {
                                             const totals = getEntityTotalsFromItems('package');
                                             return (
                                               <>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Qty:</span>
                                                   <span className="fw-bold small">{totals.totalQty || 0}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Amount:</span>
                                                   <span className="fw-bold small">{(totals.totalAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveDiscountPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalDiscountAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveTaxPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalTaxAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Net Amount:</span>
                                                   <span className="fw-bold small text-primary">{(totals.netAmount || 0).toFixed(2)}</span>
                                                 </div>
                                               </>
                                             );
                                           })()}
                                         </div>
                                       </Col>
                                     )}
                                     {form.entityItems.filter(item => item.type === 'packageSeries').length > 0 && (
                                       <Col md={3}>
                                         <div className="border rounded p-2">
                                           <h6 className="text-secondary mb-2">Package Series</h6>
                                           {(() => {
                                             const totals = getEntityTotalsFromItems('packageSeries');
                                             return (
                                               <>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Qty:</span>
                                                   <span className="fw-bold small">{totals.totalQty || 0}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Amount:</span>
                                                   <span className="fw-bold small">{(totals.totalAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveDiscountPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalDiscountAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveTaxPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalTaxAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Net Amount:</span>
                                                   <span className="fw-bold small text-primary">{(totals.netAmount || 0).toFixed(2)}</span>
                                                 </div>
                                               </>
                                             );
                                           })()}
                                         </div>
                                       </Col>
                                     )}
                                     {form.entityItems.filter(item => item.m_InstallmentId).length > 0 && (
                                       <Col md={3}>
                                         <div className="border rounded p-2">
                                           <h6 className="text-danger mb-2">Installment Payments</h6>
                                           {(() => {
                                             const totals = getInstallmentTotalsFromItems();
                                             return (
                                               <>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Qty:</span>
                                                   <span className="fw-bold small">{totals.totalQty || 0}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Amount:</span>
                                                   <span className="fw-bold small">{(totals.totalAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveDiscountPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Disc Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalDiscountAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax (%):</span>
                                                   <span className="fw-bold small">{(totals.effectiveTaxPercentage || 0).toFixed(2)}%</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Tax Amt:</span>
                                                   <span className="fw-bold small">{(totals.totalTaxAmount || 0).toFixed(2)}</span>
                                                 </div>
                                                 <div className="d-flex justify-content-between mb-1">
                                                   <span className="small">Net Amount:</span>
                                                   <span className="fw-bold small text-primary">{(totals.netAmount || 0).toFixed(2)}</span>
                                                 </div>
                                               </>
                                             );
                                           })()}
                                         </div>
                                       </Col>
                                     )}
                                   </Row>
                                 </CardBody>
                               </Card>
                             </Col>
                           </Row>
                         )}

                         {/* Overall Totals */}
                         <Row>
                           <Col md={12}>
                             <Card className="border-success">
                               <CardBody>
                                 <h6 className="card-title text-success mb-3">Overall Totals</h6>
                                 <div className="d-flex justify-content-between mb-2">
                                   <span>Total Quantity:</span>
                                   <span className="fw-bold">{form.Invoice_TotalQty}</span>
                                 </div>
                                 <div className="d-flex justify-content-between mb-2">
                                   <span>Total Amount:</span>
                                   <span className="fw-bold">{(parseFloat(form.Invoice_TotalAmount) || 0).toFixed(2)}</span>
                                 </div>
                                 <div className="d-flex justify-content-between mb-2">
                                   <span>Discount Percentage (%):</span>
                                   <span className="fw-bold">{(parseFloat(form.Invoice_TotalAmount) || 0) > 0 ? (((parseFloat(form.Invoice_DisAmount) || 0) / (parseFloat(form.Invoice_TotalAmount) || 1)) * 100).toFixed(2) : '0.00'}%</span>
                                 </div>
                                 <div className="d-flex justify-content-between mb-2">
                                   <span>Total Discount:</span>
                                   <span className="fw-bold">{(parseFloat(form.Invoice_DisAmount) || 0).toFixed(2)}</span>
                                 </div>
                                 <div className="d-flex justify-content-between mb-2">
                                   <span>Tax Percentage (%):</span>
                                   <span className="fw-bold">{((form.Invoice_TotalAmount || 0) - (form.Invoice_DisAmount || 0)) > 0 ? (((parseFloat(form.Invoice_TaxAmount) || 0) / ((form.Invoice_TotalAmount || 0) - (form.Invoice_DisAmount || 0))) * 100).toFixed(2) : '0.00'}%</span>
                                 </div>
                                 <div className="d-flex justify-content-between mb-2">
                                   <span>Total Tax:</span>
                                   <span className="fw-bold">{(parseFloat(form.Invoice_TaxAmount) || 0).toFixed(2)}</span>
                                 </div>
                                 <hr />
                                 <div className="d-flex justify-content-between mb-2">
                                   <span>Initial Net Amount:</span>
                                   <span className="fw-bold">{(parseFloat(form.Invoice_TotalAmount) - parseFloat(form.Invoice_DisAmount) + parseFloat(form.Invoice_TaxAmount) || 0).toFixed(2)}</span>
                                 </div>
                                 <div className="d-flex justify-content-between mb-2">
                                   <span>Overall Discount (%):</span>
                                   <Input
                                     type="number"
                                     step="0.01"
                                     min="0"
                                     max="100"
                                     value={formatNumericValue(form.overallDiscountPercentage)}
                                     onChange={(e) => {
                                       const value = e.target.value;
                                       setForm(prev => ({ ...prev, overallDiscountPercentage: value === '' ? null : (parseFloat(value) || 0) }));
                                     }}
                                     style={{ width: '80px', fontSize: '12px' }}
                                     readOnly={isReadOnly}
                                     disabled={isReadOnly}
                                   />
                                 </div>
                                 <div className="d-flex justify-content-between mb-2">
                                   <span>Overall Discount Amount:</span>
                                   <span className="fw-bold text-warning">{(parseFloat(form.overallDiscountAmount) || 0).toFixed(2)}</span>
                                 </div>
                                 <hr />
                                 <div className="d-flex justify-content-between">
                                   <span className="fw-bold">Final Net Amount:</span>
                                   <span className="fw-bold text-success fs-5">{(parseFloat(form.Invoice_NetAmount) || 0).toFixed(2)}</span>
                                 </div>
                               </CardBody>
                             </Card>
                           </Col>
                         </Row>

                         <Row>
                           <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Invoice_Type">Invoice Type <span className="text-danger">*</span></Label>
                              <div>
                                <div className="form-check form-check-inline">
                                  <Input
                                    className="form-check-input"
                                    type="radio"
                                    name="Invoice_Type"
                                    id="cash"
                                    value="ca"
                                    checked={form.Invoice_Type === "ca"}
                                    onChange={(e) => setForm({ ...form, Invoice_Type: e.target.value })}
                                    disabled={isReturnMode}
                                  />
                                  <Label className="form-check-label" for="cash">
                                    Cash
                                  </Label>
                                </div>
                                <div className="form-check form-check-inline">
                                  <Input
                                    className="form-check-input"
                                    type="radio"
                                    name="Invoice_Type"
                                    id="credit"
                                    value="cr"
                                    checked={form.Invoice_Type === "cr"}
                                    onChange={(e) => setForm({ ...form, Invoice_Type: e.target.value })}
                                    disabled={isReturnMode}
                                  />
                                  <Label className="form-check-label" for="credit">
                                    Credit
                                  </Label>
                                </div>
                              </div>
                            </FormGroup>
                           </Col>
                           {form.Invoice_Type === "ca" && (
                             <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label for="PaymentMethod">Payment Method <span className="text-danger">*</span></Label>
                                <div>
                                  <div className="form-check form-check-inline">
                                    <Input
                                      className="form-check-input"
                                      type="radio"
                                      name="PaymentMethod"
                                      id="creditCard"
                                      value="cc"
                                      checked={form.PaymentMethod === "cc"}
                                      onChange={(e) => {
                                        // Allow deselecting if already selected
                                        const newValue = form.PaymentMethod === "cc" ? null : e.target.value;
                                        setForm({ ...form, PaymentMethod: newValue, AuthCode: newValue ? form.AuthCode : "" });
                                      }}
                                      disabled={isReturnMode && originalInvoicePaymentMethod !== "cc"}
                                    />
                                    <Label className="form-check-label" for="creditCard">
                                      Credit Card
                                    </Label>
                                  </div>
                                  <div className="form-check form-check-inline">
                                    <Input
                                      className="form-check-input"
                                      type="radio"
                                      name="PaymentMethod"
                                      id="online"
                                      value="on"
                                      checked={form.PaymentMethod === "on"}
                                      onChange={(e) => {
                                        // Allow deselecting if already selected
                                        const newValue = form.PaymentMethod === "on" ? null : e.target.value;
                                        setForm({ ...form, PaymentMethod: newValue, AuthCode: newValue ? form.AuthCode : "" });
                                      }}
                                      disabled={isReturnMode && originalInvoicePaymentMethod !== "on"}
                                    />
                                    <Label className="form-check-label" for="online">
                                      Online
                                    </Label>
                                  </div>
                                </div>
                              </FormGroup>
                             </Col>
                           )}
                         </Row>

                        {form.Invoice_Type === "ca" && (
                          <Row>
                            <Col md={4}>
                              <FormGroup className="mb-4">
                                <Label for="CashAmount">Cash Amount</Label>
                                <Input
                                  id="CashAmount"
                                  name="CashAmount"
                                  type="number"
                                  step="0.01"
                                  value={formatNumericValue(form.CashAmount)}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const cashAmount = value === '' ? 0 : (parseFloat(value) || 0);
                                    const netAmount = form.Invoice_NetAmount || 0;
                                    
                                    // Validate that cash amount doesn't exceed net amount
                                    if (cashAmount > netAmount) {
                                      Swal.fire({
                                        icon: 'warning',
                                        title: 'Invalid Amount',
                                        text: `Cash Amount cannot exceed Net Amount (${netAmount.toFixed(2)})`,
                                        confirmButtonText: 'OK'
                                      });
                                      return;
                                    }
                                    
                                    // Calculate cashless amount and round to 2 decimal places
                                    const cashlessAmount = Math.max(0, Math.round((netAmount - cashAmount) * 100) / 100);
                                    
                                    setForm({ 
                                      ...form, 
                                      CashAmount: value === '' ? null : cashAmount,
                                      CashlessAmount: cashlessAmount === 0 ? null : cashlessAmount,
                                      PaymentMethod: cashAmount > 0 ? "ca" : form.PaymentMethod
                                    });
                                  }}
                                  placeholder="Enter cash amount"
                                  disabled={isReturnMode && (originalInvoiceType === "cr" || (originalInvoicePaymentMethod !== "ca" && originalInvoicePaymentMethod !== null))}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup className="mb-4">
                                <Label for="CashlessAmount">Cashless Amount</Label>
                                <Input
                                  id="CashlessAmount"
                                  name="CashlessAmount"
                                  type="number"
                                  step="0.01"
                                  value={formatNumericValue(form.CashlessAmount)}
                                  readOnly={isReturnMode ? (originalInvoicePaymentMethod === "ca" || originalInvoiceType === "cr") : true}
                                  onChange={isReturnMode && (originalInvoicePaymentMethod === "cc" || originalInvoicePaymentMethod === "on") ? (e) => {
                                    const value = e.target.value;
                                    const cashlessAmount = value === '' ? 0 : (parseFloat(value) || 0);
                                    const netAmount = form.Invoice_NetAmount || 0;
                                    
                                    // Validate that cashless amount doesn't exceed net amount
                                    if (cashlessAmount > netAmount) {
                                      Swal.fire({
                                        icon: 'warning',
                                        title: 'Invalid Amount',
                                        text: `Cashless Amount cannot exceed Net Amount (${netAmount.toFixed(2)})`,
                                        confirmButtonText: 'OK'
                                      });
                                      return;
                                    }
                                    
                                    // Calculate cash amount and round to 2 decimal places
                                    const cashAmount = Math.max(0, Math.round((netAmount - cashlessAmount) * 100) / 100);
                                    
                                    setForm({ 
                                      ...form, 
                                      CashlessAmount: value === '' ? null : cashlessAmount,
                                      CashAmount: cashAmount === 0 ? null : cashAmount,
                                      PaymentMethod: cashlessAmount > 0 && (form.PaymentMethod === "cc" || form.PaymentMethod === "on") ? form.PaymentMethod : "ca"
                                    });
                                  } : undefined}
                                  placeholder={isReturnMode && (originalInvoicePaymentMethod === "cc" || originalInvoicePaymentMethod === "on") ? "Enter cashless amount" : "Calculated automatically"}
                                  disabled={isReturnMode && originalInvoiceType === "cr"}
                                />
                              </FormGroup>
                            </Col>
                            {(form.PaymentMethod === "cc" || form.PaymentMethod === "on") && (
                              <Col md={4}>
                                <FormGroup className="mb-4">
                                  <Label for="AuthCode">Authentication Code</Label>
                                  <Input
                                    id="AuthCode"
                                    name="AuthCode"
                                    type="text"
                                    value={form.AuthCode}
                                    onChange={(e) => setForm({ ...form, AuthCode: e.target.value })}
                                    placeholder="Enter authentication code"
                                  />
                                </FormGroup>
                              </Col>
                            )}
                          </Row>
                        )}

                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading || !form.Invoice_CustomerId || !form.Invoice_BusinessId || isReadOnly}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isReadOnly ? "View Only" : (isEditing ? "Update Invoice" : "Save Invoice")}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleCancel}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Invoices</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={3}>
                                <Label for="filterInvoiceId">Invoice Number</Label>
                                <Input
                                  id="filterInvoiceId"
                                  name="filterInvoiceId"
                                  type="number"
                                  value={filter.Invoice_Id || ""}
                                  onChange={(e) => setFilter({ ...filter, Invoice_Id: e.target.value ? parseInt(e.target.value) : null })}
                                  placeholder="Search invoice number..."
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterCustomer">Customer Name</Label>
                                <Select
                                  value={selectedCustomerFilterOption}
                                  onChange={(selected) => {
                                    setSelectedCustomerFilterOption(selected);
                                    setFilter({ ...filter, Customer_Id: selected?.value || null });
                                  }}
                                  options={customerSearchOptions}
                                  placeholder="Type at least 3 characters to search customers..."
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                  isLoading={customerSearchLoading}
                                  noOptionsMessage={() => "Type at least 3 characters to search"}
                                  loadingMessage={() => "Searching customers..."}
                                  onInputChange={(inputValue) => {
                                    // Clear previous timeout
                                    if (searchTimeout) {
                                      clearTimeout(searchTimeout);
                                    }
                                    
                                    // Set new timeout
                                    const timeout = setTimeout(() => {
                                      searchCustomersFilterManually(inputValue);
                                    }, 300);
                                    setSearchTimeout(timeout);
                                    
                                    return inputValue;
                                  }}
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterFromDate">From Date</Label>
                                <DatePicker
                                  selected={filter.FromDate}
                                  onChange={(date) => setFilter({ ...filter, FromDate: date })}
                                  className="form-control"
                                  placeholderText="Select from date"
                                  dateFormat="dd/MM/yyyy"
                                  isClearable
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterToDate">To Date</Label>
                                <DatePicker
                                  selected={filter.ToDate}
                                  onChange={(date) => setFilter({ ...filter, ToDate: date })}
                                  className="form-control"
                                  placeholderText="Select to date"
                                  dateFormat="dd/MM/yyyy"
                                  isClearable
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterInvoiceType">Invoice Type</Label>
                                <Select
                                  value={invoiceTypeOptions.find(option => option.value === filter.Invoice_Type)}
                                  onChange={(selected) => setFilter({ ...filter, Invoice_Type: selected?.value || null })}
                                  options={invoiceTypeOptions}
                                  placeholder="Select Invoice Type"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>
                              <Col md={3}>
                                <Label for="filterPaymentMethod">Payment Method</Label>
                                <Select
                                  value={paymentMethodOptions.find(option => option.value === filter.PaymentMethod)}
                                  onChange={(selected) => setFilter({ ...filter, PaymentMethod: selected?.value || null })}
                                  options={paymentMethodOptions}
                                  placeholder="Select Payment Method"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>

                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Invoice List
                            </h5>
                            {hasEditOrHigher && (
                              <Button
                                color="primary"
                                onClick={() => {
                                  resetForm();
                                  setActiveTab("1");
                                }}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Invoice
                              </Button>
                            )}
                          </div>
                          
                          {/* Tabs for Sales Invoices and Sales Returns */}
                          <Nav tabs className="nav-tabs-custom mb-3">
                            <NavItem>
                              <NavLink
                                style={{ 
                                  cursor: "pointer",
                                  fontWeight: invoiceListType === 'normal' ? "bold" : "normal",
                                  color: invoiceListType === 'normal' ? "#556ee6" : "#74788d",
                                  borderBottom: invoiceListType === 'normal' ? "2px solid #556ee6" : "none",
                                  padding: "10px 20px",
                                  fontSize: "14px"
                                }}
                                className={invoiceListType === 'normal' ? "active" : ""}
                                onClick={() => setInvoiceListType('normal')}
                              >
                                <Receipt size={16} className="me-2" />
                                Sales Invoices
                              </NavLink>
                            </NavItem>
                            <NavItem>
                              <NavLink
                                style={{ 
                                  cursor: "pointer",
                                  fontWeight: invoiceListType === 'return' ? "bold" : "normal",
                                  color: invoiceListType === 'return' ? "#556ee6" : "#74788d",
                                  borderBottom: invoiceListType === 'return' ? "2px solid #556ee6" : "none",
                                  padding: "10px 20px",
                                  fontSize: "14px"
                                }}
                                className={invoiceListType === 'return' ? "active" : ""}
                                onClick={() => setInvoiceListType('return')}
                              >
                                <RotateCcw size={16} className="me-2" />
                                Sales Returns
                              </NavLink>
                            </NavItem>
                          </Nav>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading invoices...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">#</th>
                                      <th width="12%">Invoice Number</th>
                                      <th width="12%">Date</th>
                                      <th width="15%">Customer Name</th>
                                      <th width="12%">Company</th>
                                      <th width="10%">Branch</th>
                                      <th width="10%">Invoice Type</th>
                                      <th width="10%">Total Quantity</th>
                                      <th width="12%">Net Amount</th>
                                      <th width="12%">Payment Method</th>
                                      <th width="12%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length > 0 ? (
                                      paginatedData.map((item, index) => (
                                        <tr key={item.invoice_Id || item.Invoice_Id}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{item.invoice_No || item.Invoice_No || 'N/A'}</td>
                                          <td>{item.invoice_Date || item.Invoice_Date ? new Date(item.invoice_Date || item.Invoice_Date).toLocaleDateString('en-GB') : 'N/A'}</td>
                                          <td>{getCustomerName(item.invoice_CustomerId || item.Invoice_CustomerId)}</td>
                                          <td>{getBusinessName(item.invoice_BusinessId || item.Invoice_BusinessId)}</td>
                                          <td>{getBranchName(item.invoice_BranchId || item.Invoice_BranchId)}</td>
                                          <td>{getInvoiceTypeName(item.invoice_Type || item.Invoice_Type)}</td>
                                          <td>{item.invoice_TotalQty || item.Invoice_TotalQty || 0}</td>
                                          <td>{(item.invoice_NetAmount || item.Invoice_NetAmount || 0).toFixed(2)}</td>
                                          <td>{getPaymentMethodName(item.paymentMethod || item.PaymentMethod)}</td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                                title="View Invoice"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              <Button
                                                color="success"
                                                size="sm"
                                                onClick={() => handlePrintInvoice(item)}
                                                className="waves-effect waves-light"
                                                title="Print Invoice"
                                                disabled={printLoading}
                                              >
                                                {printLoading ? (
                                                  <ClipLoader size={12} color="#ffffff" />
                                                ) : (
                                                  <Printer size={12} />
                                                )}
                                              </Button>
                                              {/* Show Return button only for non-return invoices and if user has edit permission */}
                                              {hasEditOrHigher && (!item.isReturn && !item.IsReturn) && (
                                                <Button
                                                  color="warning"
                                                  size="sm"
                                                  onClick={() => handleSalesReturn(item)}
                                                  className="waves-effect waves-light"
                                                  title="Create Sales Return"
                                                >
                                                  <RotateCcw size={12} />
                                                </Button>
                                              )}
                                              {/* {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === (item.invoice_Id || item.Invoice_Id)}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === (item.invoice_Id || item.Invoice_Id) ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )} */}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="11" className="text-center">
                                          {hasAppliedFilter 
                                            ? "No invoices found matching the filter criteria." 
                                            : invoiceListType === 'return' 
                                              ? "No sales returns available." 
                                              : "No invoices available. Please add some invoices first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {startIndex + 1} to {Math.min(endIndex, filteredTotalCount)} of {filteredTotalCount} entries
                                    </p>
                                  </div>
                                  <div className="pagination-wrapper">
                                    <Pagination
                                      className="ant-pagination"
                                      current={page}
                                      total={filteredTotalCount}
                                      pageSize={pageSize}
                                      onChange={handlePageChange}
                                      showTitle={false}
                                    />
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal for Bulk Selection */}
      <Modal isOpen={form.showEntityModal} toggle={closeEntityModal} size="lg">
        <ModalHeader toggle={closeEntityModal}>
                                        Select {(form.modalEntityType || '').charAt(0).toUpperCase() + (form.modalEntityType || '').slice(1)}s with Quantities
        </ModalHeader>
        <ModalBody style={{ fontSize: '0.875rem' }}>
          {/* Search Input */}
          <div className="mb-3">
                                          <span style={{ fontSize: '0.875rem' }}>Search {(form.modalEntityType || '').charAt(0).toUpperCase() + (form.modalEntityType || '').slice(1)}s:</span>
            <Input
              type="text"
              placeholder={`Search for ${form.modalEntityType}s...`}
              value={form.modalSearchTerm}
              onChange={(e) => setForm(prev => ({ ...prev, modalSearchTerm: e.target.value }))}
              className="form-control"
              style={{ fontSize: '0.875rem' }}
            />
    </div>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
            {form.modalEntityType === 'product' && (() => {
              // Get already added product IDs
              const addedProductIds = form.entityItems
                .filter(item => item.type === 'product')
                .map(item => item.entityId);
              
              const filteredProducts = productOptions.filter(product => 
                product.label.toLowerCase().includes(form.modalSearchTerm.toLowerCase()) &&
                !addedProductIds.includes(product.value)
              );
              return filteredProducts.length > 0 ? filteredProducts.map(product => (
              <div key={product.value} className="border rounded" style={{ padding: '0.5rem', marginBottom: '0.5rem' }}>
                <div className="d-flex align-items-center gap-2">
                  <div className="form-check">
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      id={`product-${product.value}`}
                      checked={!!form.selectedModalItems[product.value]}
                      onChange={(e) => {
                        handleModalItemCheck(product.value, e.target.checked);
                        if (e.target.checked) {
                          setTimeout(() => {
                            const qtyInput = document.getElementById(`qty-product-${product.value}`);
                            if (qtyInput) {
                              qtyInput.focus();
                              qtyInput.select();
                            }
                          }, 100);
                        }
                      }}
                    />
                    <span className="form-check-label">
                      {product.label}
                    </span>
                  </div>
                  {form.selectedModalItems[product.value] && (
                    <div className="ms-auto d-flex align-items-center gap-2">
                      <span style={{ fontSize: '0.875rem' }}>Quantity:</span>
                      <Input
                        id={`qty-product-${product.value}`}
                        type="number"
                        min="1"
                        value={form.selectedModalItems[product.value] || ''}
                        onChange={(e) => handleModalQuantityChange(product.value, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        style={{ width: '80px', fontSize: '0.875rem' }}
                        size="sm"
                        placeholder="Qty"
                      />
                      <Button
                        color="outline-primary"
                        size="sm"
                        onClick={() => handleOpenStockModal(product.value, product.label, form.selectedModalItems[product.value])}
                        title="Select Stock"
                        style={{ padding: '4px 8px' }}
                      >
                        <Package size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )) : (
              <div className="text-center p-4 text-muted">
                <i className="fas fa-search fa-2x mb-2"></i>
                <p>No products found matching "{form.modalSearchTerm}"</p>
              </div>
            );
            })()}

            {form.modalEntityType === 'service' && (() => {
              // Get already added service IDs
              const addedServiceIds = form.entityItems
                .filter(item => item.type === 'service')
                .map(item => item.entityId);
              
              return serviceOptions
                .filter(service => 
                  service.label.toLowerCase().includes(form.modalSearchTerm.toLowerCase()) &&
                  !addedServiceIds.includes(service.value)
                )
                .map(service => (
              <div key={service.value} className="border rounded" style={{ padding: '0.5rem', marginBottom: '0.5rem' }}>
                <div className="d-flex align-items-center gap-2">
                  <div className="form-check">
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      id={`service-${service.value}`}
                      checked={!!form.selectedModalItems[service.value]}
                      onChange={(e) => {
                        handleModalItemCheck(service.value, e.target.checked);
                        if (e.target.checked) {
                          setTimeout(() => {
                            const qtyInput = document.getElementById(`qty-service-${service.value}`);
                            if (qtyInput) {
                              qtyInput.focus();
                              qtyInput.select();
                            }
                          }, 100);
                        }
                      }}
                    />
                    <span className="form-check-label">
                      {service.label}
                    </span>
                  </div>
                  {form.selectedModalItems[service.value] && (
                    <div className="ms-auto">
                      <span style={{ fontSize: '0.875rem' }}>Quantity:</span>
                      <Input
                        id={`qty-service-${service.value}`}
                        type="number"
                        min="1"
                        value={form.selectedModalItems[service.value] || ''}
                        onChange={(e) => handleModalQuantityChange(service.value, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        style={{ width: '80px', fontSize: '0.875rem' }}
                        size="sm"
                        placeholder="Qty"
                      />
                    </div>
                  )}
                </div>
              </div>
            ));
            })()}

            {form.modalEntityType === 'package' && (() => {
              // Get already added package IDs
              const addedPackageIds = form.entityItems
                .filter(item => item.type === 'package')
                .map(item => item.entityId);
              
              return packageOptions
                .filter(pkg => 
                  pkg.label.toLowerCase().includes(form.modalSearchTerm.toLowerCase()) &&
                  !addedPackageIds.includes(pkg.value)
                )
                .map(pkg => (
              <div key={pkg.value} className="border rounded" style={{ padding: '0.5rem', marginBottom: '0.5rem' }}>
                <div className="d-flex align-items-center gap-2">
                  <div className="form-check">
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      id={`package-${pkg.value}`}
                      checked={!!form.selectedModalItems[pkg.value]}
                      onChange={(e) => {
                        handleModalItemCheck(pkg.value, e.target.checked);
                        if (e.target.checked) {
                          setTimeout(() => {
                            const qtyInput = document.getElementById(`qty-package-${pkg.value}`);
                            if (qtyInput) {
                              qtyInput.focus();
                              qtyInput.select();
                            }
                          }, 100);
                        }
                      }}
                    />
                    <span className="form-check-label">
                      {pkg.label}
                    </span>
                  </div>
                  {form.selectedModalItems[pkg.value] && (
                    <div className="ms-auto">
                      <span style={{ fontSize: '0.875rem' }}>Quantity:</span>
                      <Input
                        id={`qty-package-${pkg.value}`}
                        type="number"
                        min="1"
                        value={form.selectedModalItems[pkg.value] || ''}
                        onChange={(e) => handleModalQuantityChange(pkg.value, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        style={{ width: '80px', fontSize: '0.875rem' }}
                        size="sm"
                        placeholder="Qty"
                      />
                    </div>
                  )}
                </div>
              </div>
            ));
            })()}

            {form.modalEntityType === 'packageSeries' && (() => {
              // Get already added package series IDs
              const addedPackageSeriesIds = form.entityItems
                .filter(item => item.type === 'packageSeries')
                .map(item => item.entityId);
              
              return packageSeriesOptions
                .filter(series => 
                  series.label.toLowerCase().includes(form.modalSearchTerm.toLowerCase()) &&
                  !addedPackageSeriesIds.includes(series.value)
                )
                .map(series => (
              <div key={series.value} className="border rounded" style={{ padding: '0.5rem', marginBottom: '0.5rem' }}>
                <div className="d-flex align-items-center gap-2">
                  <div className="form-check">
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      id={`packageSeries-${series.value}`}
                      checked={!!form.selectedModalItems[series.value]}
                      onChange={(e) => {
                        handleModalItemCheck(series.value, e.target.checked);
                        if (e.target.checked) {
                          setTimeout(() => {
                            const qtyInput = document.getElementById(`qty-packageSeries-${series.value}`);
                            if (qtyInput) {
                              qtyInput.focus();
                              qtyInput.select();
                            }
                          }, 100);
                        }
                      }}
                    />
                    <span className="form-check-label">
                      {series.label}
                    </span>
                  </div>
                  {form.selectedModalItems[series.value] && (
                    <div className="ms-auto">
                      <span style={{ fontSize: '0.875rem' }}>Quantity:</span>
                      <Input
                        id={`qty-packageSeries-${series.value}`}
                        type="number"
                        min="1"
                        value={form.selectedModalItems[series.value] || ''}
                        onChange={(e) => handleModalQuantityChange(series.value, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        style={{ width: '80px', fontSize: '0.875rem' }}
                        size="sm"
                        placeholder="Qty"
                      />
                    </div>
                  )}
                </div>
              </div>
            ));
            })()}
          </div>
        </ModalBody>
        <ModalFooter style={{ fontSize: '0.875rem' }}>
          <div className="d-flex justify-content-between w-100">
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              Selected: {Object.keys(form.selectedModalItems).filter(key => form.selectedModalItems[key] > 0).length} items
            </span>
            <div className="d-flex gap-2">
              <Button color="secondary" onClick={closeEntityModal} style={{ fontSize: '0.875rem' }}>
                Cancel
              </Button>
              <Button 
                color="success" 
                onClick={addSelectedModalItems}
                disabled={Object.entries(form.selectedModalItems).filter(([key, quantity]) => {
                  if (quantity === undefined || quantity === null || quantity === '') return false;
                  const qty = parseInt(quantity);
                  return !isNaN(qty) && qty > 0;
                }).length === 0}
                style={{ fontSize: '0.875rem' }}
              >
                Add Selected Items
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Customer Membership Modal */}
      <Modal isOpen={form.showMembershipModal} toggle={closeMembershipModal} size="lg">
        <ModalHeader toggle={closeMembershipModal}>
          Select Membership Installments
        </ModalHeader>
        <ModalBody style={{ fontSize: '0.875rem' }}>
          {form.customerMemberships.length > 0 ? (
            <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '0.875rem' }}>
              {form.customerMemberships.map((membership, membershipIndex) => (
                <div key={membership.membershipId} className="mb-3">
                  <div className="border rounded" style={{ padding: '0.75rem' }}>
                    <h6 className="text-primary mb-2" style={{ fontSize: '0.875rem' }}>
                      {membership.entity_Type === 'PAC' ? 'Package' : 'Package Series'} Membership
                    </h6>
                    <div className="mb-2" style={{ fontSize: '0.875rem' }}>
                      {membership.entity_Type === 'PAC' ? 'Package' : 'Series'}: {form.entityNames[membership.entity_Id] || `${membership.entity_Type === 'PAC' ? 'Package' : 'Series'} ${membership.entity_Id}`} | 
                      Purchase Date: {new Date(membership.purchaseDate).toLocaleDateString('en-GB')}
                    </div>
                    
                    {membership.installments && membership.installments.length > 0 ? (
                      <div>
                        <h6 className="text-secondary mb-2" style={{ fontSize: '0.875rem' }}>Available Installments:</h6>
                        {membership.installments
                          .filter(installment => (Math.round((installment.amount - installment.paidAmount) * 100) / 100) > 0)
                          .map((installment, installmentIndex) => {
                          const installmentName = form.installmentNames[installment.installmentId] || `Installment ${installment.installmentId}`;
                          const isSelected = form.selectedMembershipInstallments[installment.installmentId]?.checked || false;
                          const amount = form.selectedMembershipInstallments[installment.installmentId]?.amount ?? '';
                          
                          return (
                            <div key={installment.installmentId} className="d-flex align-items-center justify-content-between mb-1 p-2 border rounded" style={{ fontSize: '0.875rem' }}>
                              <div className="form-check">
                                <Input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`installment-${installment.installmentId}`}
                                  checked={isSelected}
                                  onChange={(e) => {
                                    handleMembershipInstallmentCheck(installment.installmentId, e.target.checked);
                                    if (e.target.checked) {
                                      // Set default amount to remaining amount when checked
                                      const remainingAmount = Math.round((installment.amount - installment.paidAmount) * 100) / 100;
                                      handleMembershipInstallmentAmountChange(installment.installmentId, remainingAmount);
                                    }
                                  }}
                                />
                                <span className="form-check-label">
                                  {installmentName}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                                  Amount: {installment.amount.toFixed(2)} | 
                                  Billed: {installment.paidAmount.toFixed(2)} | 
                                  Remaining: {(Math.round((installment.amount - installment.paidAmount) * 100) / 100).toFixed(2)}
                                </div>
                                {isSelected && (
                                  <div className="d-flex flex-column gap-1">
                                    <div className="d-flex align-items-center gap-1">
                                      <span className="mb-0" style={{ fontSize: '0.875rem' }}>Amount:</span>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={amount}
                                        onChange={(e) => handleMembershipInstallmentAmountChange(installment.installmentId, e.target.value)}
                                        style={{ 
                                          width: '100px', 
                                          fontSize: '0.875rem',
                                          borderColor: form.selectedMembershipInstallments[installment.installmentId]?.validationError ? '#dc3545' : '#495057' 
                                        }}
                                        size="sm"
                                        placeholder="Amount"
                                      />
                                    </div>
                                    {form.selectedMembershipInstallments[installment.installmentId]?.validationError && (
                                      <div className="text-danger" style={{ fontSize: '0.875rem' }}>
                                        {form.selectedMembershipInstallments[installment.installmentId].validationError}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-muted" style={{ fontSize: '0.875rem' }}>No installments available for this membership.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted py-4" style={{ fontSize: '0.875rem' }}>
              No memberships found for this customer.
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ fontSize: '0.875rem' }}>
          <div className="d-flex justify-content-between w-100">
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              Selected: {Object.values(form.selectedMembershipInstallments).filter(item => item.checked).length} installments
            </span>
            <div className="d-flex gap-2">
              <Button color="secondary" onClick={closeMembershipModal} style={{ fontSize: '0.875rem' }}>
                Cancel
              </Button>
              <Button 
                color="success" 
                onClick={addSelectedMembershipInstallments}
                disabled={Object.values(form.selectedMembershipInstallments).filter(item => item.checked && item.amount > 0).length === 0}
                style={{ fontSize: '0.875rem' }}
              >
                Add Selected Installments
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Quick Add Customer Modal */}
      <QuickAddCustomer
        isOpen={showQuickAddCustomer}
        toggle={() => setShowQuickAddCustomer(false)}
        onCustomerAdded={handleCustomerAdded}
      />

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={showCustomerProfile}
        toggle={() => setShowCustomerProfile(false)}
        customerId={selectedCustomerOption?.value || null}
        customer={selectedCustomerOption ? { customer_Id: selectedCustomerOption.value } : null}
      />

      {/* Stock Selection Modal */}
      <StockSelectionModal
        key={`stock-modal-${form.currentStockProduct?.id || 'none'}`}
        isOpen={form.showStockModal}
        toggle={() => setForm(prev => ({ ...prev, showStockModal: false, preloadedStockItems: null }))}
        productId={form.currentStockProduct?.id}
        productName={form.currentStockProduct?.name}
        requestedQuantity={form.currentStockProduct?.quantity}
        onStockSelected={handleStockSelected}
        selectedStockItems={form.selectedStockData[form.currentStockProduct?.id] || []}
        branchId={form.Invoice_BranchId}
        businessId={form.Invoice_BusinessId}
        preloadedStockItems={form.preloadedStockItems || null}
        temporaryFormUniqueId={temporaryFormUniqueId}
        flag={isReturnMode ? 8 : 7}
        createdBy={getUserSession().userId}
      />

      {/* Stock Details Modal */}
      <StockDetailsModal
        isOpen={form.showStockDetailsModal}
        toggle={() => {
          // Restore SweetAlert z-index when closing stock modal
          const sweetAlertContainers = document.querySelectorAll('.swal2-container');
          sweetAlertContainers.forEach(container => {
            container.style.zIndex = '1060';
          });
          setForm(prev => ({ ...prev, showStockDetailsModal: false }));
        }}
        productName={form.currentStockDetailsProduct}
        stockDetails={form.currentStockDetails}
      />

      {/* Invoice Print Modal */}
      {invoicePrintData && (
        <InvoicePrintModal
          isOpen={showInvoicePrintModal}
          toggle={() => {
            setShowInvoicePrintModal(false);
            setInvoicePrintData(null);
          }}
          invoiceData={invoicePrintData}
        />
      )}
    </div>
  );
}